import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Point, Wall, Opening, Soffit, Obstacle, ResizeHandle } from '../types/room';
import { GRID_SIZE, snapToGrid, snapPointToAngle, distance, formatDistance, isNearPoint, findClosestWall, getPointOnWall, getOffsetPoint, snapToNearestWall } from '../utils/geometry';
import { SunRing } from './SunRing';
import { RoomTransformControls } from './RoomTransformControls';
import { ElementResizeHandles } from './ElementResizeHandles';
import { DimensionLabel } from './DimensionLabel';
import { FloatingActionMenu } from './FloatingActionMenu';
import { EdgeProjectionDimensions } from './EdgeProjectionDimensions';
import { Canvas3D } from './Canvas3D';
import { motion, AnimatePresence } from 'framer-motion';
import { WallTemplate } from './TemplateDrawer';
import { RotateCw, Move } from 'lucide-react';
type HandleType = 'start' | 'end' | 'middle' | null;
interface CanvasProps {
  walls: Wall[];
  openings: Opening[];
  soffits: Soffit[];
  obstacles: Obstacle[];
  activeTool: string;
  isDrawing: boolean;
  drawingPoints: Point[];
  isDrawingSoffit: boolean;
  soffitPoints: Point[];
  selectedObjectId: string | null;
  selectedTemplate: WallTemplate;
  selectedSoffitTemplate: WallTemplate;
  isPlacingTemplate: boolean;
  isPlacingSoffitTemplate: boolean;
  templateStart: Point | null;
  soffitTemplateStart: Point | null;
  templateEnd: Point | null;
  zoom: number;
  pan: Point;
  sunPosition: number;
  sunRadius: number;
  sunCenter: Point;
  isDraggingObstacle: boolean;
  draggedObstacleId: string | null;
  showGuideLines: boolean;
  isRotatingRoom: boolean;
  isMovingRoom: boolean;
  roomRotation: number;
  viewMode: 'top' | '3d';
  roomHeight: number;
  onStartDrawing: (point: Point) => void;
  onContinueDrawing: (point: Point) => void;
  onFinishDrawing: (endPoint: Point) => void;
  onSelectObject: (id: string | null) => void;
  onUpdateWall: (id: string, updates: Partial<Wall>) => void;
  onMoveAllWalls: (offset: Point) => void;
  onAddOpening: (opening: Opening) => void;
  onAddObstacle: (obstacle: Obstacle) => void;
  onAddSoffit: (soffit: Soffit) => void;
  onUpdateObstacle: (id: string, updates: Partial<Obstacle>) => void;
  onDeleteObject: (id: string) => void;
  onSetDraggingObstacle: (dragging: boolean) => void;
  onSetDraggedObstacleId: (id: string | null) => void;
  onSunPositionChange: (degrees: number) => void;
  onSunRadiusChange: (radius: number) => void;
  onSunCenterChange: (center: Point) => void;
  onPanChange: (pan: Point) => void;
  onZoomChange: (zoom: number) => void;
  onTemplatePreview: (start: Point, end: Point) => Point[];
  onSoffitTemplatePreview: (start: Point, end: Point) => Point[];
  onApplyRoomRotation: (angleDelta: number) => void;
  onMoveRoom: (offset: Point) => void;
  onSetRotatingRoom: (rotating: boolean) => void;
  onSetMovingRoom: (moving: boolean) => void;
}
export function Canvas({
  walls,
  openings,
  soffits,
  obstacles,
  activeTool,
  isDrawing,
  drawingPoints,
  isDrawingSoffit,
  soffitPoints,
  selectedObjectId,
  selectedTemplate,
  selectedSoffitTemplate,
  isPlacingTemplate,
  isPlacingSoffitTemplate,
  templateStart,
  soffitTemplateStart,
  templateEnd,
  zoom,
  pan,
  sunPosition,
  sunRadius,
  sunCenter,
  isDraggingObstacle,
  draggedObstacleId,
  showGuideLines,
  isRotatingRoom,
  isMovingRoom,
  roomRotation,
  viewMode = 'top',
  roomHeight = 280,
  onStartDrawing,
  onContinueDrawing,
  onFinishDrawing,
  onSelectObject,
  onUpdateWall,
  onMoveAllWalls,
  onAddOpening,
  onAddObstacle,
  onAddSoffit,
  onUpdateObstacle,
  onDeleteObject,
  onSetDraggingObstacle,
  onSetDraggedObstacleId,
  onSunPositionChange,
  onSunRadiusChange,
  onSunCenterChange,
  onPanChange,
  onZoomChange,
  onTemplatePreview,
  onSoffitTemplatePreview,
  onApplyRoomRotation,
  onMoveRoom,
  onSetRotatingRoom,
  onSetMovingRoom
}: CanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [mousePos, setMousePos] = useState<Point>({
    x: 0,
    y: 0
  });
  const [snappedPos, setSnappedPos] = useState<Point>({
    x: 0,
    y: 0
  });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<Point>({
    x: 0,
    y: 0
  });
  // Wall handle dragging
  const [isDraggingHandle, setIsDraggingHandle] = useState(false);
  const [draggedHandle, setDraggedHandle] = useState<{
    wallId: string;
    type: HandleType;
  }>({
    wallId: '',
    type: null
  });
  // Opening dragging along wall - SIMPLIFIED
  const [isDraggingOpening, setIsDraggingOpening] = useState(false);
  const [draggedOpeningId, setDraggedOpeningId] = useState<string | null>(null);
  // Soffit dragging state - NEW
  const [isDraggingSoffit, setIsDraggingSoffit] = useState(false);
  const [draggedSoffitId, setDraggedSoffitId] = useState<string | null>(null);
  const [soffitDragStart, setSoffitDragStart] = useState<Point | null>(null);
  // Element resizing
  const [isResizing, setIsResizing] = useState(false);
  const [resizeElementId, setResizeElementId] = useState<string | null>(null);
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle | null>(null);
  const [resizeStartBounds, setResizeStartBounds] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [resizeStartMouse, setResizeStartMouse] = useState<Point | null>(null);
  const [isObstacleGrabbed, setIsObstacleGrabbed] = useState(false);
  const [grabbedObstacleId, setGrabbedObstacleId] = useState<string | null>(null);
  // NOTE: isDraggingObstacle and draggedObstacleId are PROPS, not local state
  const [isMovingAll, setIsMovingAll] = useState(false);
  const [moveAllStart, setMoveAllStart] = useState<Point>({
    x: 0,
    y: 0
  });
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [hoveredWallId, setHoveredWallId] = useState<string | null>(null);
  const [closestWall, setClosestWall] = useState<{
    wall: Wall;
    position: number;
  } | null>(null);
  const [templatePreviewPoints, setTemplatePreviewPoints] = useState<Point[]>([]);
  const [soffitTemplatePreviewPoints, setSoffitTemplatePreviewPoints] = useState<Point[]>([]);
  const [rotateStartAngle, setRotateStartAngle] = useState(0);
  const [moveStartPos, setMoveStartPos] = useState<Point | null>(null);
  // Double-click detection for finishing drawing
  const [lastClickTime, setLastClickTime] = useState(0);
  const [lastClickPos, setLastClickPos] = useState<Point | null>(null);
  const DOUBLE_CLICK_THRESHOLD = 300; // milliseconds
  const DOUBLE_CLICK_DISTANCE = 10; // pixels
  const width = 2000;
  const height = 2000;
  const centerX = width / 2;
  const centerY = height / 2;
  // Dimension editing
  const [editingDimension, setEditingDimension] = useState<{
    id: string;
    type: 'width' | 'height' | 'depth' | 'length';
  } | null>(null);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftPressed(true);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftPressed(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    onZoomChange(Math.max(0.3, Math.min(3, zoom * delta)));
  }, [zoom, onZoomChange]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('wheel', handleWheel, {
        passive: false
      });
      return () => canvas.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);
  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width * width - centerX;
    const y = (e.clientY - rect.top) / rect.height * height - centerY;
    setMousePos({
      x,
      y
    });
    // Handle resizing
    if (isResizing && resizeElementId && resizeHandle && resizeStartBounds && resizeStartMouse) {
      const snappedMouse = snapToGrid({
        x,
        y
      });
      const dx = snappedMouse.x - resizeStartMouse.x;
      const dy = snappedMouse.y - resizeStartMouse.y;
      // Check if resizing a soffit point
      if (resizeHandle.startsWith('point-')) {
        const pointIndex = parseInt(resizeHandle.split('-')[1]);
        const soffit = soffits.find(s => s.id === resizeElementId);
        if (soffit) {
          const newPoints = [...soffit.points];
          newPoints[pointIndex] = snappedMouse;
          onUpdateObstacle(resizeElementId, {
            points: newPoints
          });
        }
        return;
      }
      // NEW: Check if resizing a soffit edge
      if (resizeHandle.startsWith('edge-')) {
        const edgeIndex = parseInt(resizeHandle.split('-')[1]);
        const soffit = soffits.find(s => s.id === resizeElementId);
        if (soffit) {
          const newPoints = [...soffit.points];
          const nextIndex = (edgeIndex + 1) % soffit.points.length;
          // Move both points of the edge
          newPoints[edgeIndex] = {
            x: newPoints[edgeIndex].x + dx,
            y: newPoints[edgeIndex].y + dy
          };
          newPoints[nextIndex] = {
            x: newPoints[nextIndex].x + dx,
            y: newPoints[nextIndex].y + dy
          };
          onUpdateObstacle(resizeElementId, {
            points: newPoints
          });
          // Update start mouse for next frame
          setResizeStartMouse(snappedMouse);
        }
        return;
      }
      // Find the element being resized
      const obstacle = obstacles.find(o => o.id === resizeElementId);
      const opening = openings.find(o => o.id === resizeElementId);
      if (obstacle) {
        let newWidth = resizeStartBounds.width;
        let newDepth = resizeStartBounds.height;
        let newX = resizeStartBounds.x;
        let newY = resizeStartBounds.y;
        // Apply resize based on handle
        if (resizeHandle.includes('e')) {
          newWidth = Math.max(20, resizeStartBounds.width + dx);
        }
        if (resizeHandle.includes('w')) {
          newWidth = Math.max(20, resizeStartBounds.width - dx);
          newX = resizeStartBounds.x + dx;
        }
        if (resizeHandle.includes('s')) {
          newDepth = Math.max(20, resizeStartBounds.height + dy);
        }
        if (resizeHandle.includes('n')) {
          newDepth = Math.max(20, resizeStartBounds.height - dy);
          newY = resizeStartBounds.y + dy;
        }
        onUpdateObstacle(resizeElementId, {
          width: newWidth,
          depth: newDepth,
          position: {
            x: newX + newWidth / 2,
            y: newY + newDepth / 2
          }
        });
      } else if (opening) {
        let newWidth = resizeStartBounds.width;
        let newHeight = resizeStartBounds.height;
        if (resizeHandle.includes('e') || resizeHandle.includes('w')) {
          newWidth = Math.max(20, resizeStartBounds.width + (resizeHandle.includes('e') ? dx : -dx));
        }
        if (resizeHandle.includes('s') || resizeHandle.includes('n')) {
          newHeight = Math.max(20, resizeStartBounds.height + (resizeHandle.includes('s') ? dy : -dy));
        }
        onUpdateObstacle(resizeElementId, {
          width: newWidth,
          height: newHeight
        });
      }
      return;
    }
    // Handle opening dragging along wall - FIXED
    if (isDraggingOpening && draggedOpeningId) {
      const opening = openings.find(o => o.id === draggedOpeningId);
      const wall = walls.find(w => w.id === opening?.wallId);
      if (opening && wall) {
        const snappedMouse = snapToGrid({
          x,
          y
        });
        // Find closest wall (allow wall switching)
        const closest = findClosestWall(snappedMouse, walls);
        if (closest && closest.distance < 100) {
          const targetWall = closest.wall;
          const wallLength = distance(targetWall.start, targetWall.end);
          // Constrain position to wall bounds
          const newPosition = Math.max(opening.width / 2, Math.min(wallLength - opening.width / 2, closest.position));
          // Update opening with new wall and position
          onUpdateObstacle(draggedOpeningId, {
            wallId: targetWall.id,
            position: newPosition
          });
        }
      }
      return;
    }
    // Handle soffit dragging - NEW: Use update instead of add
    if (isDraggingSoffit && draggedSoffitId && soffitDragStart) {
      const soffit = soffits.find(s => s.id === draggedSoffitId);
      if (soffit) {
        const snappedMouse = snapToGrid({
          x,
          y
        });
        const offset = {
          x: snappedMouse.x - soffitDragStart.x,
          y: snappedMouse.y - soffitDragStart.y
        };
        // Move all soffit points by the offset
        const newPoints = soffit.points.map(p => ({
          x: p.x + offset.x,
          y: p.y + offset.y
        }));
        // Update the soffit using the generic update function
        onUpdateObstacle(draggedSoffitId, {
          points: newPoints
        });
        // Update drag start for next frame
        setSoffitDragStart(snappedMouse);
      }
      return;
    }
    // Handle obstacle dragging - FIXED: Actually move the obstacle
    if (isDraggingObstacle && draggedObstacleId) {
      const obstacle = obstacles.find(o => o.id === draggedObstacleId);
      if (obstacle) {
        const snappedMouse = snapToGrid({
          x,
          y
        });
        // Update obstacle position
        onUpdateObstacle(draggedObstacleId, {
          position: snappedMouse
        });
      }
      return;
    }
    // Handle walls
    if (isMovingAll) {
      const snappedMouse = snapToGrid({
        x,
        y
      });
      const offset = {
        x: snappedMouse.x - moveAllStart.x,
        y: snappedMouse.y - moveAllStart.y
      };
      onMoveAllWalls(offset);
      setMoveAllStart(snappedMouse);
      return;
    }
    if (isDraggingHandle && draggedHandle.wallId && draggedHandle.type) {
      const wall = walls.find(w => w.id === draggedHandle.wallId);
      if (wall) {
        const snappedMouse = snapToGrid({
          x,
          y
        });
        if (draggedHandle.type === 'start') {
          onUpdateWall(draggedHandle.wallId, {
            start: snappedMouse
          });
        } else if (draggedHandle.type === 'end') {
          onUpdateWall(draggedHandle.wallId, {
            end: snappedMouse
          });
        } else if (draggedHandle.type === 'middle') {
          const midPoint = {
            x: (wall.start.x + wall.end.x) / 2,
            y: (wall.start.y + wall.end.y) / 2
          };
          const offset = {
            x: snappedMouse.x - midPoint.x,
            y: snappedMouse.y - midPoint.y
          };
          onUpdateWall(draggedHandle.wallId, {
            start: {
              x: wall.start.x + offset.x,
              y: wall.start.y + offset.y
            },
            end: {
              x: wall.end.x + offset.x,
              y: wall.end.y + offset.y
            }
          });
        }
      }
      return;
    }
    if (isPanning) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      onPanChange({
        x: pan.x + dx,
        y: pan.y + dy
      });
      setPanStart({
        x: e.clientX,
        y: e.clientY
      });
      return;
    }
    if (isRotatingRoom && rotateStartAngle !== null) {
      const angle = Math.atan2(y, x) * (180 / Math.PI);
      const deltaAngle = angle - rotateStartAngle;
      onApplyRoomRotation(deltaAngle);
      setRotateStartAngle(angle);
      return;
    }
    if (isMovingRoom && moveStartPos) {
      const snappedMouse = snapToGrid({
        x,
        y
      });
      const offset = {
        x: snappedMouse.x - moveStartPos.x,
        y: snappedMouse.y - moveStartPos.y
      };
      onMoveRoom(offset);
      setMoveStartPos(snappedMouse);
      return;
    }
    let snapped = snapToGrid({
      x,
      y
    });
    if (isPlacingTemplate && templateStart && selectedTemplate !== 'free') {
      const previewPoints = onTemplatePreview(templateStart, snapped);
      setTemplatePreviewPoints(previewPoints);
    }
    if (isPlacingSoffitTemplate && soffitTemplateStart && selectedSoffitTemplate !== 'free') {
      const previewPoints = onSoffitTemplatePreview(soffitTemplateStart, snapped);
      setSoffitTemplatePreviewPoints(previewPoints);
    }
    if (activeTool === 'wall' && selectedTemplate === 'free' && drawingPoints.length > 0) {
      const lastPoint = drawingPoints[drawingPoints.length - 1];
      snapped = snapPointToAngle(lastPoint, snapped);
      snapped = snapToGrid(snapped);
    }
    if (activeTool === 'wall' && selectedTemplate === 'free' && drawingPoints.length > 2) {
      const firstPoint = drawingPoints[0];
      if (isNearPoint(snapped, firstPoint, 30)) {
        snapped = firstPoint;
      }
    }
    if (activeTool === 'soffit' && selectedSoffitTemplate === 'free' && soffitPoints.length > 2) {
      const firstPoint = soffitPoints[0];
      if (isNearPoint(snapped, firstPoint, 30)) {
        snapped = firstPoint;
      }
    }
    if ((activeTool === 'window' || activeTool === 'door') && walls.length > 0) {
      const closest = findClosestWall({
        x,
        y
      }, walls);
      // Increase snap distance from 50 to 100 for easier placement
      if (closest && closest.distance < 100) {
        setClosestWall({
          wall: closest.wall,
          position: closest.position
        });
      } else {
        setClosestWall(null);
      }
    }
    setSnappedPos(snapped);
    if (isDrawing && activeTool === 'wall' && selectedTemplate === 'free') {
      onContinueDrawing(snapped);
    }
  }, [activeTool, drawingPoints, soffitPoints, isDrawing, isPanning, isDraggingHandle, isMovingAll, isDraggingOpening, isDraggingObstacle, isDraggingSoffit, isResizing, isObstacleGrabbed, draggedOpeningId, draggedObstacleId, draggedSoffitId, soffitDragStart, grabbedObstacleId, resizeElementId, resizeHandle, resizeStartBounds, resizeStartMouse, moveAllStart, draggedHandle, panStart, pan, walls, openings, obstacles, soffits, selectedTemplate, selectedSoffitTemplate, isPlacingTemplate, isPlacingSoffitTemplate, templateStart, soffitTemplateStart, onContinueDrawing, onPanChange, onTemplatePreview, onSoffitTemplatePreview, onUpdateWall, onMoveAllWalls, onUpdateObstacle, onAddSoffit, isRotatingRoom, isMovingRoom, rotateStartAngle, moveStartPos, roomRotation, onApplyRoomRotation, onMoveRoom, width, centerX, centerY]);
  const handleMouseDown = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button === 1) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({
        x: e.clientX,
        y: e.clientY
      });
      return;
    }
    const rect = svgRef.current?.getBoundingClientRect();
    if (rect) {
      const x = (e.clientX - rect.left) / rect.width * width - centerX;
      const y = (e.clientY - rect.top) / rect.height * height - centerY;
      if (isRotatingRoom) {
        const angle = Math.atan2(y, x) * (180 / Math.PI);
        setRotateStartAngle(angle);
        return;
      }
      if (isMovingRoom) {
        const snapped = snapToGrid({
          x,
          y
        });
        setMoveStartPos(snapped);
        return;
      }
      if (e.button === 0 && isShiftPressed && activeTool === 'select' && walls.length > 0) {
        const snapped = snapToGrid({
          x,
          y
        });
        setIsMovingAll(true);
        setMoveAllStart(snapped);
      }
    }
  }, [isShiftPressed, activeTool, walls.length, width, centerX, centerY, isRotatingRoom, isMovingRoom]);
  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    setIsDraggingHandle(false);
    setIsDraggingOpening(false);
    setIsDraggingSoffit(false);
    onSetDraggingObstacle(false);
    setIsResizing(false);
    setIsMovingAll(false);
    setDraggedHandle({
      wallId: '',
      type: null
    });
    setDraggedOpeningId(null);
    setDraggedSoffitId(null);
    setSoffitDragStart(null);
    onSetDraggedObstacleId(null);
    setResizeElementId(null);
    setResizeHandle(null);
    setResizeStartBounds(null);
    setResizeStartMouse(null);
    setRotateStartAngle(0);
    setMoveStartPos(null);
    onSetRotatingRoom(false);
    onSetMovingRoom(false);
  }, [onSetRotatingRoom, onSetMovingRoom, onSetDraggingObstacle, onSetDraggedObstacleId]);
  const handleHandleMouseDown = useCallback((e: React.MouseEvent, wallId: string, handleType: HandleType) => {
    e.stopPropagation();
    setIsDraggingHandle(true);
    setDraggedHandle({
      wallId,
      type: handleType
    });
  }, []);
  const handleResizeStart = useCallback((elementId: string, handle: ResizeHandle, bounds: {
    x: number;
    y: number;
    width: number;
    height: number;
  }, e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = svgRef.current?.getBoundingClientRect();
    if (rect) {
      const x = (e.clientX - rect.left) / rect.width * width - centerX;
      const y = (e.clientY - rect.top) / rect.height * height - centerY;
      setIsResizing(true);
      setResizeElementId(elementId);
      setResizeHandle(handle);
      setResizeStartBounds(bounds);
      setResizeStartMouse(snapToGrid({
        x,
        y
      }));
    }
  }, [width, centerX, centerY]);
  const handleOpeningMouseDown = useCallback((e: React.MouseEvent, openingId: string) => {
    e.stopPropagation();
    if (activeTool === 'select') {
      setIsDraggingOpening(true);
      setDraggedOpeningId(openingId);
      onSelectObject(openingId);
    }
  }, [activeTool, onSelectObject]);
  const handleObstacleMouseDown = useCallback((e: React.MouseEvent, obstacleId: string) => {
    e.stopPropagation();
    if (activeTool === 'select') {
      // Start dragging immediately on mouse down
      onSetDraggingObstacle(true);
      onSetDraggedObstacleId(obstacleId);
      onSelectObject(obstacleId);
    }
  }, [activeTool, onSelectObject, onSetDraggingObstacle, onSetDraggedObstacleId]);
  // NEW: Soffit mouse down handler
  const handleSoffitMouseDown = useCallback((e: React.MouseEvent, soffitId: string) => {
    e.stopPropagation();
    if (activeTool === 'select') {
      const rect = svgRef.current?.getBoundingClientRect();
      if (rect) {
        const x = (e.clientX - rect.left) / rect.width * width - centerX;
        const y = (e.clientY - rect.top) / rect.height * height - centerY;
        const snappedMouse = snapToGrid({
          x,
          y
        });
        setIsDraggingSoffit(true);
        setDraggedSoffitId(soffitId);
        setSoffitDragStart(snappedMouse);
        onSelectObject(soffitId);
      }
    }
  }, [activeTool, onSelectObject, width, centerX, centerY]);
  const handleSoffitEdgeMouseDown = useCallback((e: React.MouseEvent, soffitId: string, edgeIndex: number) => {
    e.stopPropagation();
    const rect = svgRef.current?.getBoundingClientRect();
    if (rect) {
      const x = (e.clientX - rect.left) / rect.width * width - centerX;
      const y = (e.clientY - rect.top) / rect.height * height - centerY;
      setIsResizing(true);
      setResizeElementId(soffitId);
      setResizeHandle(`edge-${edgeIndex}` as any);
      setResizeStartMouse(snapToGrid({
        x,
        y
      }));
    }
  }, [width, centerX, centerY]);
  const handleClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const target = e.target as SVGElement;
    console.log('=== SVG CLICKED ===', {
      activeTool,
      targetTag: target.tagName,
      targetElement: target.getAttribute('data-element'),
      closestWall: closestWall ? 'EXISTS' : 'NULL'
    });
    // Only block clicks on interactive handles/labels
    if (target.tagName === 'circle' || target.tagName === 'text' || target.classList.contains('pointer-events-none')) {
      console.log('Blocked: interactive element');
      return;
    }
    // Double-click detection
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTime;
    const isDoubleClick = timeSinceLastClick < DOUBLE_CLICK_THRESHOLD && lastClickPos && Math.abs(snappedPos.x - lastClickPos.x) < DOUBLE_CLICK_DISTANCE && Math.abs(snappedPos.y - lastClickPos.y) < DOUBLE_CLICK_DISTANCE;
    setLastClickTime(now);
    setLastClickPos(snappedPos);
    // Select tool - deselect
    if (activeTool === 'select') {
      console.log('Select tool: deselecting');
      onSelectObject(null);
      return;
    }
    // Window/Door placement - SIMPLIFIED
    if (activeTool === 'window' || activeTool === 'door') {
      console.log('Window/Door tool active');
      if (closestWall) {
        console.log('Creating opening on wall:', closestWall.wall.id);
        const opening: Opening = {
          id: `opening-${Date.now()}`,
          type: activeTool === 'window' ? 'window' : 'door',
          wallId: closestWall.wall.id,
          position: closestWall.position,
          width: activeTool === 'window' ? 100 : 80,
          height: activeTool === 'window' ? 100 : 210,
          elevation: activeTool === 'window' ? 105 : 0
        };
        onAddOpening(opening);
        console.log('Opening created!');
      } else {
        console.log('No closest wall found');
      }
      return;
    }
    // Obstacle placement
    if (activeTool === 'obstacle') {
      console.log('Creating obstacle at', snappedPos);
      const obstacle: Obstacle = {
        id: `obstacle-${Date.now()}`,
        position: snappedPos,
        width: 100,
        height: 100,
        depth: 100
      };
      onAddObstacle(obstacle);
      console.log('Obstacle created!');
      return;
    }
    // Soffit drawing
    if (activeTool === 'soffit') {
      console.log('Soffit tool - template:', selectedSoffitTemplate, 'placing:', isPlacingSoffitTemplate);
      if (selectedSoffitTemplate !== 'free') {
        if (!isPlacingSoffitTemplate) {
          console.log('Starting soffit template');
          onStartDrawing(snappedPos);
        } else {
          console.log('Finishing soffit template');
          onFinishDrawing(snappedPos);
          setSoffitTemplatePreviewPoints([]);
        }
      } else {
        if (isDoubleClick && soffitPoints.length >= 3) {
          console.log('Finishing free soffit');
          onFinishDrawing(snappedPos);
        } else {
          console.log('Adding soffit point');
          onStartDrawing(snappedPos);
        }
      }
      return;
    }
    // Wall drawing
    if (activeTool === 'wall') {
      console.log('Wall tool active');
      if (selectedTemplate !== 'free') {
        if (!isPlacingTemplate) {
          onStartDrawing(snappedPos);
        } else {
          onFinishDrawing(snappedPos);
          setTemplatePreviewPoints([]);
        }
      } else {
        if (isDoubleClick && drawingPoints.length >= 2) {
          onFinishDrawing(snappedPos);
        } else {
          onStartDrawing(snappedPos);
        }
      }
    }
  }, [activeTool, isDrawing, isDrawingSoffit, soffitPoints, isPlacingTemplate, isPlacingSoffitTemplate, snappedPos, drawingPoints, selectedTemplate, selectedSoffitTemplate, closestWall, lastClickTime, lastClickPos, onStartDrawing, onFinishDrawing, onSelectObject, onAddOpening, onAddObstacle, setSoffitTemplatePreviewPoints]);
  const currentSegmentLength = drawingPoints.length > 0 && isDrawing && selectedTemplate === 'free' ? distance(drawingPoints[drawingPoints.length - 1], snappedPos) : 0;
  const templateWidth = isPlacingTemplate && templateStart ? Math.abs(snappedPos.x - templateStart.x) : 0;
  const templateHeight = isPlacingTemplate && templateStart ? Math.abs(snappedPos.y - templateStart.y) : 0;
  const roomCenter = useMemo(() => {
    if (walls.length === 0) return {
      x: 0,
      y: 0
    };
    let minX = Infinity,
      maxX = -Infinity;
    let minY = Infinity,
      maxY = -Infinity;
    walls.forEach(wall => {
      minX = Math.min(minX, wall.start.x, wall.end.x);
      maxX = Math.max(maxX, wall.start.x, wall.end.x);
      minY = Math.min(minY, wall.start.y, wall.end.y);
      maxY = Math.max(maxY, wall.start.y, wall.end.y);
    });
    return {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2
    };
  }, [walls]);
  // If 3D mode, render 3D view
  if (viewMode === '3d') {
    return <Canvas3D walls={walls} openings={openings} obstacles={obstacles} soffits={soffits} roomHeight={roomHeight} roomRotation={roomRotation} data-id="element-42" />;
  }
  return <div ref={canvasRef} className="flex-1 bg-[#1E1E1E] relative overflow-hidden pointer-events-auto" style={{
    cursor: isPanning ? 'grabbing' : isMovingAll ? 'move' : isDraggingHandle || isDraggingOpening || isResizing ? 'move' : isDraggingObstacle || isObstacleGrabbed ? 'move' : (activeTool === 'window' || activeTool === 'door') && closestWall ? 'copy' : activeTool !== 'select' ? 'crosshair' : 'default'
  }} data-id="element-43">
      <svg ref={svgRef} width="100%" height="100%" viewBox={`${centerX - width / (2 * zoom)} ${centerY - height / (2 * zoom)} ${width / zoom} ${height / zoom}`} onMouseMove={handleMouseMove} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp} onClick={handleClick} className={activeTool !== 'select' && !isPanning ? 'cursor-crosshair' : ''} style={{
      pointerEvents: 'auto'
    }} data-id="element-44">
        <defs data-id="element-45">
          <pattern id="grid" width={GRID_SIZE} height={GRID_SIZE} patternUnits="userSpaceOnUse" data-id="element-46">
            <path d={`M ${GRID_SIZE} 0 L 0 0 0 ${GRID_SIZE}`} fill="none" stroke="#2a2a2a" strokeWidth="0.3" opacity="0.3" data-id="element-47" />
          </pattern>
          <pattern id="grid-major" width={GRID_SIZE * 10} height={GRID_SIZE * 10} patternUnits="userSpaceOnUse" data-id="element-48">
            <rect width={GRID_SIZE * 10} height={GRID_SIZE * 10} fill="url(#grid)" data-id="element-49" />
            <path d={`M ${GRID_SIZE * 10} 0 L 0 0 0 ${GRID_SIZE * 10}`} fill="none" stroke="#3a3a3a" strokeWidth="0.5" opacity="0.3" data-id="element-50" />
          </pattern>

          <pattern id="obstacle-pattern" width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-51">
            <line x1="0" y1="0" x2="0" y2="10" stroke="#EF5350" strokeWidth="2" strokeOpacity="0.6" data-id="element-52" />
          </pattern>

          <pattern id="obstacle-pattern-wall" width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-53">
            <line x1="0" y1="0" x2="0" y2="10" stroke="#FFA726" strokeWidth="2" strokeOpacity="0.6" data-id="element-54" />
          </pattern>

          <pattern id="obstacle-pattern-neutral" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-55">
            <line x1="0" y1="0" x2="0" y2="8" stroke="#B0B0B0" strokeWidth="1.5" strokeOpacity="0.4" data-id="element-56" />
          </pattern>

          <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="5" refY="5" orient="auto" data-id="element-57">
            <polygon points="0 0, 10 5, 0 10" fill="#66BB6A" data-id="element-58" />
          </marker>

          <filter id="selected-glow" x="-50%" y="-50%" width="200%" height="200%" data-id="element-59">
            <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" data-id="element-60" />
            <feFlood floodColor="#4FC3F7" floodOpacity="0.8" result="color" data-id="element-61" />
            <feComposite in="color" in2="blur" operator="in" result="glow" data-id="element-62" />
            <feMerge data-id="element-63">
              <feMergeNode in="glow" data-id="element-64" />
              <feMergeNode in="glow" data-id="element-65" />
              <feMergeNode in="SourceGraphic" data-id="element-66" />
            </feMerge>
          </filter>
        </defs>

        <rect width={width} height={height} fill="url(#grid-major)" data-id="element-67" />

        <line x1={centerX - 20} y1={centerY} x2={centerX + 20} y2={centerY} stroke="#4A4A4A" strokeWidth="1" data-id="element-68" />
        <line x1={centerX} y1={centerY - 20} x2={centerX} y2={centerY + 20} stroke="#4A4A4A" strokeWidth="1" data-id="element-69" />

        <SunRing sunPosition={sunPosition} onSunPositionChange={onSunPositionChange} centerX={sunCenter.x} centerY={sunCenter.y} radius={sunRadius} onRadiusChange={onSunRadiusChange} onCenterChange={onSunCenterChange} data-id="element-70" />

        {/* Rotation Button - Fixed position above room center, does NOT rotate with room */}
        {walls.length > 0 && <g data-id="element-71">
            {/* Position button CLOSER to room center - 120px instead of 200px */}
            <g transform={`translate(${roomCenter.x + centerX}, ${roomCenter.y + centerY - 120})`} style={{
          cursor: isRotatingRoom ? 'grabbing' : 'grab'
        }} onMouseDown={e => {
          e.stopPropagation();
          // Start rotation mode
          onSetRotatingRoom(true);
          const rect = svgRef.current?.getBoundingClientRect();
          if (rect) {
            const x = (e.clientX - rect.left) / rect.width * width - centerX;
            const y = (e.clientY - rect.top) / rect.height * height - centerY;
            const angle = Math.atan2(y - (roomCenter.y - 120), x - roomCenter.x) * (180 / Math.PI);
            setRotateStartAngle(angle);
          }
        }} onClick={e => {
          e.stopPropagation();
          if (!isRotatingRoom) {
            // Click without drag: rotate by 45°
            onApplyRoomRotation(45);
          }
        }} data-id="element-72">
              {/* Button background with hover effect */}
              <motion.rect x={-60} y={-22} width={120} height={44} rx={8} fill="#1e1e1e" stroke="#4FC3F7" strokeWidth={2} whileHover={{
            fill: '#2a2a2a',
            strokeWidth: 2.5
          }} animate={{
            scale: isRotatingRoom ? 1.05 : 1
          }} style={{
            filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.5))'
          }} data-id="element-73" />

              {/* Rotate icon */}
              <g transform="translate(-35, 0)" data-id="element-74">
                <motion.path d="M -6 -6 A 8 8 0 1 1 -6 6 M -6 6 L -3 3 M -6 6 L -9 3" stroke="#4FC3F7" strokeWidth={2} fill="none" strokeLinecap="round" strokeLinejoin="round" animate={{
              rotate: isRotatingRoom ? 360 : 0
            }} transition={{
              duration: 2,
              repeat: isRotatingRoom ? Infinity : 0,
              ease: 'linear'
            }} data-id="element-75" />
              </g>

              {/* Rotation angle text - EDITABLE on double click */}
              <g onDoubleClick={e => {
            e.stopPropagation();
            setEditingDimension({
              id: 'room-rotation',
              type: 'length'
            });
          }} data-id="element-76">
                {editingDimension?.id === 'room-rotation' ? <foreignObject x={-20} y={-15} width={80} height={30} data-id="element-77">
                    <input type="number" defaultValue={Math.round(roomRotation)} autoFocus onBlur={e => {
                const newAngle = Number(e.target.value);
                const currentAngle = roomRotation;
                const delta = newAngle - currentAngle;
                onApplyRoomRotation(delta);
                setEditingDimension(null);
              }} onKeyDown={e => {
                if (e.key === 'Enter') {
                  const newAngle = Number((e.target as HTMLInputElement).value);
                  const currentAngle = roomRotation;
                  const delta = newAngle - currentAngle;
                  onApplyRoomRotation(delta);
                  setEditingDimension(null);
                } else if (e.key === 'Escape') {
                  setEditingDimension(null);
                }
              }} className="w-full h-full bg-[#2D2D2D] border-2 border-[#4FC3F7] rounded px-2 text-white text-sm font-bold text-center focus:outline-none pointer-events-auto" style={{
                fontFamily: 'monospace'
              }} data-id="element-78" />
                  </foreignObject> : <text x={15} y={5} fill="#4FC3F7" fontSize={16} fontWeight="700" fontFamily="monospace" textAnchor="middle" className="pointer-events-auto cursor-text" data-id="element-79">
                    {Math.round(roomRotation)}°
                  </text>}
              </g>

              {/* Drag hint text */}
              {!isRotatingRoom && <text x={0} y={35} fill="#888" fontSize={10} fontFamily="monospace" textAnchor="middle" className="pointer-events-none" opacity={0.7} data-id="element-80">
                  Click: +45° • Drag: Rotate • Double-click: Edit
                </text>}
            </g>
          </g>}

        {/* Walls - Permanent walls that have been placed */}
        <AnimatePresence data-id="element-81">
          {walls.map(wall => {
          const isSelected = selectedObjectId === wall.id;
          const isHovered = hoveredWallId === wall.id;
          const wallLength = distance(wall.start, wall.end);
          // Calculate wall rectangle vertices for solid rendering
          const wallThickness = 35;
          const dx = wall.end.x - wall.start.x;
          const dy = wall.end.y - wall.start.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          const perpX = -dy / length * (wallThickness / 2);
          const perpY = dx / length * (wallThickness / 2);
          const wallPoints = [{
            x: wall.start.x + perpX,
            y: wall.start.y + perpY
          }, {
            x: wall.end.x + perpX,
            y: wall.end.y + perpY
          }, {
            x: wall.end.x - perpX,
            y: wall.end.y - perpY
          }, {
            x: wall.start.x - perpX,
            y: wall.start.y - perpY
          }];
          // Find openings on this wall for boolean subtraction
          const wallOpenings = openings.filter(o => o.wallId === wall.id);
          return <motion.g key={wall.id} data-element="wall" data-element-id={wall.id} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 25
          }} data-id="element-82">
                {/* Wall with boolean cuts for openings */}
                <defs data-id="element-83">
                  <clipPath id={`wall-clip-${wall.id}`} data-id="element-84">
                    {/* Start with full wall rectangle */}
                    <rect x={Math.min(...wallPoints.map(p => p.x)) + centerX} y={Math.min(...wallPoints.map(p => p.y)) + centerY} width={Math.max(...wallPoints.map(p => p.x)) - Math.min(...wallPoints.map(p => p.x))} height={Math.max(...wallPoints.map(p => p.y)) - Math.min(...wallPoints.map(p => p.y))} data-id="element-85" />
                    {/* Subtract openings */}
                    {wallOpenings.map(opening => {
                  const ratio = opening.position / wallLength;
                  const x = wall.start.x + (wall.end.x - wall.start.x) * ratio;
                  const y = wall.start.y + (wall.end.y - wall.start.y) * ratio;
                  const wallAngle = Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x);
                  const angleDeg = wallAngle * 180 / Math.PI;
                  return <g key={opening.id} transform={`translate(${x + centerX} ${y + centerY}) rotate(${angleDeg})`} data-id="element-86">
                          <rect x={-opening.width / 2} y={-wallThickness / 2 - 2} width={opening.width} height={wallThickness + 4} fill="black" data-id="element-87" />
                        </g>;
                })}
                  </clipPath>
                </defs>

                {/* Solid wall with cuts */}
                <polygon points={wallPoints.map(p => `${p.x + centerX},${p.y + centerY}`).join(' ')} fill="#3A3A3A" clipPath={`url(#wall-clip-${wall.id})`} className="cursor-pointer" style={{
              transition: 'all 0.2s',
              cursor: activeTool === 'select' && !isSelected ? 'move' : 'pointer'
            }} onMouseEnter={() => setHoveredWallId(wall.id)} onMouseLeave={() => setHoveredWallId(null)} onMouseDown={e => {
              // If select tool and wall is NOT selected, start moving all walls
              if (activeTool === 'select' && !isSelected) {
                e.stopPropagation();
                const rect = svgRef.current?.getBoundingClientRect();
                if (rect) {
                  const x = (e.clientX - rect.left) / rect.width * width - centerX;
                  const y = (e.clientY - rect.top) / rect.height * height - centerY;
                  const snapped = snapToGrid({
                    x,
                    y
                  });
                  setIsMovingAll(true);
                  setMoveAllStart(snapped);
                }
              }
            }} onClick={e => {
              // ONLY handle select tool here
              if (activeTool === 'select') {
                e.stopPropagation();
                onSelectObject(wall.id);
              }
              // For window/door/soffit - let it bubble to SVG handleClick
            }} data-id="element-88" />

                {/* Wall outline */}
                <polygon points={wallPoints.map(p => `${p.x + centerX},${p.y + centerY}`).join(' ')} fill="none" stroke={isSelected ? '#00E5FF' : isHovered ? '#D1F4FF' : '#E0E0E0'} strokeWidth={isSelected ? 2.5 : 2} strokeLinejoin="miter" clipPath={`url(#wall-clip-${wall.id})`} className="cursor-pointer pointer-events-none" style={{
              transition: 'all 0.2s',
              filter: isSelected ? 'drop-shadow(0 0 8px rgba(0, 229, 255, 0.6))' : undefined
            }} data-id="element-89" />

                {/* Corner circles */}
                {!isSelected && <>
                    <circle cx={wall.start.x + centerX} cy={wall.start.y + centerY} r={8} fill="#4a4a4a" stroke="#E0E0E0" strokeWidth={1.5} className="pointer-events-none" data-id="element-90" />
                    <circle cx={wall.start.x + centerX} cy={wall.start.y + centerY} r={4} fill="#2a2a2a" className="pointer-events-none" data-id="element-91" />

                    <circle cx={wall.end.x + centerX} cy={wall.end.y + centerY} r={8} fill="#4a4a4a" stroke="#E0E0E0" strokeWidth={1.5} className="pointer-events-none" data-id="element-92" />
                    <circle cx={wall.end.x + centerX} cy={wall.end.y + centerY} r={4} fill="#2a2a2a" className="pointer-events-none" data-id="element-93" />
                  </>}

                {isSelected && activeTool === 'select' && <motion.g initial={{
              opacity: 0,
              scale: 0
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 25
            }} data-id="element-94">
                    {/* Start point handle */}
                    <circle cx={wall.start.x + centerX} cy={wall.start.y + centerY} r={12} fill="#4a4a4a" stroke="#00E5FF" strokeWidth={2} className="cursor-ew-resize" onMouseDown={e => handleHandleMouseDown(e, wall.id, 'start')} data-id="element-95" />
                    <circle cx={wall.start.x + centerX} cy={wall.start.y + centerY} r={6} fill="#2a2a2a" className="pointer-events-none" data-id="element-96" />

                    {/* End point handle */}
                    <circle cx={wall.end.x + centerX} cy={wall.end.y + centerY} r={12} fill="#4a4a4a" stroke="#00E5FF" strokeWidth={2} className="cursor-ew-resize" onMouseDown={e => handleHandleMouseDown(e, wall.id, 'end')} data-id="element-97" />
                    <circle cx={wall.end.x + centerX} cy={wall.end.y + centerY} r={6} fill="#2a2a2a" className="pointer-events-none" data-id="element-98" />

                    {/* Middle handle */}
                    <g onMouseDown={e => handleHandleMouseDown(e, wall.id, 'middle')} data-id="element-99">
                      <rect x={(wall.start.x + wall.end.x) / 2 + centerX - 6} y={(wall.start.y + wall.end.y) / 2 + centerY - 6} width={12} height={12} fill="#00E5FF" stroke="#FFFFFF" strokeWidth={1.5} className="cursor-move" data-id="element-100" />
                      <path d={`M ${(wall.start.x + wall.end.x) / 2 + centerX - 3} ${(wall.start.y + wall.end.y) / 2 + centerY} L ${(wall.start.x + wall.end.x) / 2 + centerX + 3} ${(wall.start.y + wall.end.y) / 2 + centerY} M ${(wall.start.x + wall.end.x) / 2 + centerX} ${(wall.start.y + wall.end.y) / 2 + centerY - 3} L ${(wall.start.x + wall.end.x) / 2 + centerX} ${(wall.start.y + wall.end.y) / 2 + centerY + 3}`} stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" className="pointer-events-none" data-id="element-101" />
                    </g>
                  </motion.g>}
              </motion.g>;
        })}
        </AnimatePresence>

        {/* Guide Lines */}
        {showGuideLines && walls.length > 1 && <g opacity={0.5} data-id="element-102"></g>}

        {/* External Dimension Lines - Clean architectural style */}
        {showGuideLines && walls.length > 0 && <g data-id="element-103">
            {walls.map(wall => {
          const wallThickness = 35;
          const dx = wall.end.x - wall.start.x;
          const dy = wall.end.y - wall.start.y;
          const outerLength = Math.sqrt(dx * dx + dy * dy);
          const angle = Math.atan2(dy, dx);
          // Calculate room center to determine inward direction
          let roomCenterX = 0,
            roomCenterY = 0;
          walls.forEach(w => {
            roomCenterX += (w.start.x + w.end.x) / 2;
            roomCenterY += (w.start.y + w.end.y) / 2;
          });
          roomCenterX /= walls.length;
          roomCenterY /= walls.length;
          // Wall midpoint
          const wallMidX = (wall.start.x + wall.end.x) / 2;
          const wallMidY = (wall.start.y + wall.end.y) / 2;
          // Vector from room center to wall midpoint
          const toWallX = wallMidX - roomCenterX;
          const toWallY = wallMidY - roomCenterY;
          // Perpendicular to wall
          const perpAngle = angle + Math.PI / 2;
          const perpX = Math.cos(perpAngle);
          const perpY = Math.sin(perpAngle);
          // Determine if perpendicular points outward
          const dotProduct = perpX * toWallX + perpY * toWallY;
          const outwardMultiplier = dotProduct > 0 ? 1 : -1;
          const inwardMultiplier = -outwardMultiplier;
          // Calculate INNER corner points (half wall thickness inward)
          const innerOffset = wallThickness / 2 * inwardMultiplier;
          const innerStart = {
            x: wall.start.x + innerOffset * Math.cos(perpAngle),
            y: wall.start.y + innerOffset * Math.sin(perpAngle)
          };
          const innerEnd = {
            x: wall.end.x + innerOffset * Math.cos(perpAngle),
            y: wall.end.y + innerOffset * Math.sin(perpAngle)
          };
          // Calculate INNER length between inner corners
          const innerDx = innerEnd.x - innerStart.x;
          const innerDy = innerEnd.y - innerStart.y;
          const innerLength = Math.sqrt(innerDx * innerDx + innerDy * innerDy);
          // FIXED: Dimension line INSIDE, but text OUTSIDE
          const dimLineOffset = 8 * inwardMultiplier; // Line inside wall
          const textOffset = 50 * outwardMultiplier; // Text outside wall
          // Extension lines from INNER corners
          const extensionStart = 0; // Start from inner corner
          const extensionEnd = 60 * outwardMultiplier; // Extend outside
          const extStart1 = {
            x: innerStart.x + extensionStart * Math.cos(perpAngle),
            y: innerStart.y + extensionStart * Math.sin(perpAngle)
          };
          const extEnd1 = {
            x: innerStart.x + extensionEnd * Math.cos(perpAngle),
            y: innerStart.y + extensionEnd * Math.sin(perpAngle)
          };
          const extStart2 = {
            x: innerEnd.x + extensionStart * Math.cos(perpAngle),
            y: innerEnd.y + extensionStart * Math.sin(perpAngle)
          };
          const extEnd2 = {
            x: innerEnd.x + extensionEnd * Math.cos(perpAngle),
            y: innerEnd.y + extensionEnd * Math.sin(perpAngle)
          };
          // Dimension line endpoints (INSIDE the wall, along inner edge)
          const dimStart = {
            x: innerStart.x + dimLineOffset * Math.cos(perpAngle),
            y: innerStart.y + dimLineOffset * Math.sin(perpAngle)
          };
          const dimEnd = {
            x: innerEnd.x + dimLineOffset * Math.cos(perpAngle),
            y: innerEnd.y + dimLineOffset * Math.sin(perpAngle)
          };
          // Text position (OUTSIDE the wall)
          const textPos = {
            x: (innerStart.x + innerEnd.x) / 2 + textOffset * Math.cos(perpAngle),
            y: (innerStart.y + innerEnd.y) / 2 + textOffset * Math.sin(perpAngle)
          };
          return <g key={`ext-dim-${wall.id}`} opacity={0.7} data-id="element-104">
                  {/* Extension line 1 - from inner corner, extending outside */}
                  <line x1={extStart1.x + centerX} y1={extStart1.y + centerY} x2={extEnd1.x + centerX} y2={extEnd1.y + centerY} stroke="#888888" strokeWidth="0.5" className="pointer-events-none" data-id="element-105" />

                  {/* Extension line 2 - from inner corner, extending outside */}
                  <line x1={extStart2.x + centerX} y1={extStart2.y + centerY} x2={extEnd2.x + centerX} y2={extEnd2.y + centerY} stroke="#888888" strokeWidth="0.5" className="pointer-events-none" data-id="element-106" />

                  {/* Main dimension line - INSIDE the wall, along inner edge */}
                  <line x1={dimStart.x + centerX} y1={dimStart.y + centerY} x2={dimEnd.x + centerX} y2={dimEnd.y + centerY} stroke="#FFFFFF" strokeWidth="1" className="pointer-events-none" data-id="element-107" />

                  {/* Dimension text - OUTSIDE the wall - EDITABLE */}
                  <g className="cursor-pointer" onDoubleClick={e => {
              e.stopPropagation();
              setEditingDimension({
                id: wall.id,
                type: 'length'
              });
            }} data-id="element-108">
                    {editingDimension?.id === wall.id && editingDimension?.type === 'length' ? <foreignObject x={textPos.x + centerX - 50} y={textPos.y + centerY - 20} width={100} height={40} data-id="element-109">
                        <input type="number" defaultValue={Math.round(innerLength)} autoFocus onBlur={e => {
                  const newLength = Number(e.target.value);
                  if (newLength > 50) {
                    // Calculate new end point based on new length
                    const currentLength = distance(wall.start, wall.end);
                    const scale = newLength / currentLength;
                    const dx = wall.end.x - wall.start.x;
                    const dy = wall.end.y - wall.start.y;
                    onUpdateWall(wall.id, {
                      end: {
                        x: wall.start.x + dx * scale,
                        y: wall.start.y + dy * scale
                      }
                    });
                  }
                  setEditingDimension(null);
                }} onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const newLength = Number((e.target as HTMLInputElement).value);
                    if (newLength > 50) {
                      const currentLength = distance(wall.start, wall.end);
                      const scale = newLength / currentLength;
                      const dx = wall.end.x - wall.start.x;
                      const dy = wall.end.y - wall.start.y;
                      onUpdateWall(wall.id, {
                        end: {
                          x: wall.start.x + dx * scale,
                          y: wall.start.y + dy * scale
                        }
                      });
                    }
                    setEditingDimension(null);
                  } else if (e.key === 'Escape') {
                    setEditingDimension(null);
                  }
                }} className="w-full h-full bg-[#2D2D2D] border-2 border-[#4FC3F7] rounded px-3 text-white text-lg font-bold text-center focus:outline-none pointer-events-auto" data-id="element-110" />
                      </foreignObject> : <>
                        {/* Background box for better readability */}
                        <rect x={textPos.x + centerX - 40} y={textPos.y + centerY - 16} width={80} height={32} fill="#2D2D2D" stroke="#4FC3F7" strokeWidth={1.5} rx={4} opacity={0.95} className="pointer-events-auto" data-id="element-111" />
                        <text x={textPos.x + centerX} y={textPos.y + centerY + 6} fill="#FFFFFF" fontSize="16" fontWeight="700" fontFamily="Inter, sans-serif" textAnchor="middle" className="pointer-events-auto" data-id="element-112">
                          {formatDistance(innerLength)}
                        </text>
                      </>}
                  </g>
                </g>;
        })}
          </g>}

        {/* Free drawing preview - show walls as they're being drawn */}
        {isDrawing && selectedTemplate === 'free' && drawingPoints.length > 0 && <g data-id="element-113">
              {/* Draw wall segments between all points */}
              {drawingPoints.map((point, i) => {
          let endPoint;
          if (i === drawingPoints.length - 1) {
            // Last point - draw to mouse cursor
            endPoint = snappedPos;
          } else {
            // Draw to next point
            endPoint = drawingPoints[i + 1];
          }
          // Calculate wall rectangle vertices (same as real walls)
          const wallThickness = 35;
          const dx = endPoint.x - point.x;
          const dy = endPoint.y - point.y;
          const outerLength = Math.sqrt(dx * dx + dy * dy);
          if (outerLength === 0) return null;
          const angle = Math.atan2(dy, dx);
          const perpAngle = angle + Math.PI / 2;
          const perpX = -dy / outerLength * (wallThickness / 2);
          const perpY = dx / outerLength * (wallThickness / 2);
          const wallPoints = [{
            x: point.x + perpX,
            y: point.y + perpY
          }, {
            x: endPoint.x + perpX,
            y: endPoint.y + perpY
          }, {
            x: endPoint.x - perpX,
            y: endPoint.y - perpY
          }, {
            x: point.x - perpX,
            y: point.y - perpY
          }];
          const isLastSegment = i === drawingPoints.length - 1;
          // FIXED: Calculate INNER corner points for dimension
          // Assume inward is toward negative perpendicular (simplified for drawing)
          const innerOffset = wallThickness / 2;
          const innerStart = {
            x: point.x - perpX,
            y: point.y - perpY
          };
          const innerEnd = {
            x: endPoint.x - perpX,
            y: endPoint.y - perpY
          };
          // Calculate INNER length between inner corners
          const innerDx = innerEnd.x - innerStart.x;
          const innerDy = innerEnd.y - innerStart.y;
          const innerLength = Math.sqrt(innerDx * innerDx + innerDy * innerDy);
          // Label position (OUTSIDE the wall)
          const labelOffset = 40;
          const labelX = (point.x + endPoint.x) / 2 + Math.cos(perpAngle) * labelOffset;
          const labelY = (point.y + endPoint.y) / 2 + Math.sin(perpAngle) * labelOffset;
          return <motion.g key={`preview-wall-${i}`} data-id="element-114">
                    {/* Wall fill */}
                    <polygon points={wallPoints.map(p => `${p.x + centerX},${p.y + centerY}`).join(' ')} fill={isLastSegment ? 'rgba(58, 58, 58, 0.6)' : '#3A3A3A'} className="pointer-events-none" data-id="element-115" />

                    {/* Wall outline */}
                    <polygon points={wallPoints.map(p => `${p.x + centerX},${p.y + centerY}`).join(' ')} fill="none" stroke={isLastSegment ? '#00E5FF' : '#E0E0E0'} strokeWidth={2} strokeDasharray={isLastSegment ? '8,4' : '0'} strokeLinejoin="miter" className="pointer-events-none" data-id="element-116" />

                    {/* Dynamic dimension label - INNER LENGTH between inner corners */}
                    <g className="pointer-events-none" data-id="element-117">
                      <rect x={labelX + centerX - 30} y={labelY + centerY - 12} width={60} height={24} fill="#1a1a1a" stroke={isLastSegment ? '#00E5FF' : '#E0E0E0'} strokeWidth={1} rx={3} opacity={0.95} data-id="element-118" />
                      <text x={labelX + centerX} y={labelY + centerY + 5} fill="#FFFFFF" fontSize={12} fontWeight="700" fontFamily="monospace" textAnchor="middle" data-id="element-119">
                        {formatDistance(innerLength)}
                      </text>
                    </g>
                  </motion.g>;
        })}

              {/* Draw corner circles at each point */}
              {drawingPoints.map((point, i) => <g key={`preview-corner-${i}`} data-id="element-120">
                  <circle cx={point.x + centerX} cy={point.y + centerY} r={8} fill={i === 0 ? '#10b981' : '#4a4a4a'} stroke={i === 0 ? '#FFFFFF' : '#E0E0E0'} strokeWidth={2} className="pointer-events-none" data-id="element-121" />
                  <circle cx={point.x + centerX} cy={point.y + centerY} r={4} fill={i === 0 ? '#059669' : '#2a2a2a'} className="pointer-events-none" data-id="element-122" />
                </g>)}

              {/* Show potential closing wall if near first point - VISUAL ONLY, NOT ADDED */}
              {drawingPoints.length > 2 && isNearPoint(snappedPos, drawingPoints[0], 30) && <motion.g animate={{
          opacity: [0.6, 1, 0.6]
        }} transition={{
          duration: 1,
          repeat: Infinity
        }} data-id="element-123">
                    {(() => {
            const lastPoint = drawingPoints[drawingPoints.length - 1];
            const firstPoint = drawingPoints[0];
            const wallThickness = 35;
            const dx = firstPoint.x - lastPoint.x;
            const dy = firstPoint.y - lastPoint.y;
            const outerLength = Math.sqrt(dx * dx + dy * dy);
            const angle = Math.atan2(dy, dx);
            const perpAngle = angle + Math.PI / 2;
            const perpX = -dy / outerLength * (wallThickness / 2);
            const perpY = dx / outerLength * (wallThickness / 2);
            const wallPoints = [{
              x: lastPoint.x + perpX,
              y: lastPoint.y + perpY
            }, {
              x: firstPoint.x + perpX,
              y: firstPoint.y + perpY
            }, {
              x: firstPoint.x - perpX,
              y: firstPoint.y - perpY
            }, {
              x: lastPoint.x - perpX,
              y: lastPoint.y - perpY
            }];
            // FIXED: Calculate INNER length for closing wall
            const innerStart = {
              x: lastPoint.x - perpX,
              y: lastPoint.y - perpY
            };
            const innerEnd = {
              x: firstPoint.x - perpX,
              y: firstPoint.y - perpY
            };
            const innerDx = innerEnd.x - innerStart.x;
            const innerDy = innerEnd.y - innerStart.y;
            const innerLength = Math.sqrt(innerDx * innerDx + innerDy * innerDy);
            return <>
                          <polygon points={wallPoints.map(p => `${p.x + centerX},${p.y + centerY}`).join(' ')} fill="rgba(16, 185, 129, 0.3)" className="pointer-events-none" data-id="element-124" />
                          <polygon points={wallPoints.map(p => `${p.x + centerX},${p.y + centerY}`).join(' ')} fill="none" stroke="#10b981" strokeWidth={3} strokeDasharray="8,4" strokeLinejoin="miter" className="pointer-events-none" data-id="element-125" />

                          {/* Dimension for closing wall - INNER LENGTH between inner corners */}
                          <g className="pointer-events-none" data-id="element-126">
                            <rect x={(lastPoint.x + firstPoint.x) / 2 + centerX - 30} y={(lastPoint.y + firstPoint.y) / 2 + centerY - 12} width={60} height={24} fill="#1a1a1a" stroke="#10b981" strokeWidth={1} rx={3} opacity={0.95} data-id="element-127" />
                            <text x={(lastPoint.x + firstPoint.x) / 2 + centerX} y={(lastPoint.y + firstPoint.y) / 2 + centerY + 5} fill="#10b981" fontSize={12} fontWeight="700" fontFamily="monospace" textAnchor="middle" data-id="element-128">
                              {formatDistance(innerLength)}
                            </text>
                          </g>
                        </>;
          })()}
                  </motion.g>}
            </g>}

        {/* Template preview for shapes (Rectangle, L-Shape, etc) */}
        {isPlacingTemplate && templateStart && templatePreviewPoints.length > 0 && <g data-id="element-129">
              {templatePreviewPoints.map((point, i) => {
          if (i === templatePreviewPoints.length - 1) return null;
          const nextPoint = templatePreviewPoints[i + 1];
          // Draw as proper walls
          const wallThickness = 35;
          const dx = nextPoint.x - point.x;
          const dy = nextPoint.y - point.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          if (length === 0) return null;
          const perpX = -dy / length * (wallThickness / 2);
          const perpY = dx / length * (wallThickness / 2);
          const wallPoints = [{
            x: point.x + perpX,
            y: point.y + perpY
          }, {
            x: nextPoint.x + perpX,
            y: nextPoint.y + perpY
          }, {
            x: nextPoint.x - perpX,
            y: nextPoint.y - perpY
          }, {
            x: point.x - perpX,
            y: point.y - perpY
          }];
          return <g key={`template-preview-${i}`} data-id="element-130">
                    <polygon points={wallPoints.map(p => `${p.x + centerX},${p.y + centerY}`).join(' ')} fill="rgba(58, 58, 58, 0.6)" className="pointer-events-none" data-id="element-131" />
                    <polygon points={wallPoints.map(p => `${p.x + centerX},${p.y + centerY}`).join(' ')} fill="none" stroke="#00E5FF" strokeWidth={2} strokeDasharray="8,4" strokeLinejoin="miter" className="pointer-events-none" data-id="element-132" />
                  </g>;
        })}
            </g>}

        {/* Soffit template preview */}
        {isPlacingSoffitTemplate && soffitTemplateStart && soffitTemplatePreviewPoints.length > 0 && <g data-id="element-133">
              {/* Draw preview polygon */}
              <polygon points={soffitTemplatePreviewPoints.map(p => `${p.x + centerX},${p.y + centerY}`).join(' ')} fill="rgba(139, 92, 246, 0.2)" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="8,4" className="pointer-events-none" data-id="element-134" />

              {/* Draw dimensions for each edge */}
              {soffitTemplatePreviewPoints.map((point, i) => {
          const nextPoint = soffitTemplatePreviewPoints[(i + 1) % soffitTemplatePreviewPoints.length];
          const dx = nextPoint.x - point.x;
          const dy = nextPoint.y - point.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          if (length < 10) return null;
          const midX = (point.x + nextPoint.x) / 2;
          const midY = (point.y + nextPoint.y) / 2;
          return <g key={`soffit-preview-dim-${i}`} className="pointer-events-none" data-id="element-135">
                    <rect x={midX + centerX - 30} y={midY + centerY - 12} width={60} height={24} fill="#1a1a1a" stroke="#8b5cf6" strokeWidth={1} rx={3} opacity={0.95} data-id="element-136" />
                    <text x={midX + centerX} y={midY + centerY + 5} fill="#8b5cf6" fontSize={12} fontWeight="700" fontFamily="monospace" textAnchor="middle" data-id="element-137">
                      {formatDistance(length)}
                    </text>
                  </g>;
        })}
            </g>}

        {/* Free soffit drawing preview */}
        {isDrawingSoffit && selectedSoffitTemplate === 'free' && soffitPoints.length > 0 && <g data-id="element-138">
              {/* Draw lines between points */}
              {soffitPoints.map((point, i) => {
          let endPoint;
          if (i === soffitPoints.length - 1) {
            endPoint = snappedPos;
          } else {
            endPoint = soffitPoints[i + 1];
          }
          const dx = endPoint.x - point.x;
          const dy = endPoint.y - point.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          if (length === 0) return null;
          const isLastSegment = i === soffitPoints.length - 1;
          const midX = (point.x + endPoint.x) / 2;
          const midY = (point.y + endPoint.y) / 2;
          return <g key={`soffit-preview-${i}`} data-id="element-139">
                    {/* Line */}
                    <line x1={point.x + centerX} y1={point.y + centerY} x2={endPoint.x + centerX} y2={endPoint.y + centerY} stroke={isLastSegment ? '#8b5cf6' : '#a78bfa'} strokeWidth={2} strokeDasharray={isLastSegment ? '8,4' : '0'} className="pointer-events-none" data-id="element-140" />

                    {/* Dimension label */}
                    <g className="pointer-events-none" data-id="element-141">
                      <rect x={midX + centerX - 30} y={midY + centerY - 12} width={60} height={24} fill="#1a1a1a" stroke={isLastSegment ? '#8b5cf6' : '#a78bfa'} strokeWidth={1} rx={3} opacity={0.95} data-id="element-142" />
                      <text x={midX + centerX} y={midY + centerY + 5} fill={isLastSegment ? '#8b5cf6' : '#a78bfa'} fontSize={12} fontWeight="700" fontFamily="monospace" textAnchor="middle" data-id="element-143">
                        {formatDistance(length)}
                      </text>
                    </g>
                  </g>;
        })}

              {/* Draw corner points */}
              {soffitPoints.map((point, i) => <g key={`soffit-corner-${i}`} data-id="element-144">
                  <circle cx={point.x + centerX} cy={point.y + centerY} r={6} fill={i === 0 ? '#8b5cf6' : '#a78bfa'} stroke="white" strokeWidth={2} className="pointer-events-none" data-id="element-145" />
                </g>)}

              {/* Show closing line if near first point */}
              {soffitPoints.length > 2 && isNearPoint(snappedPos, soffitPoints[0], 30) && <motion.g animate={{
          opacity: [0.6, 1, 0.6]
        }} transition={{
          duration: 1,
          repeat: Infinity
        }} data-id="element-146">
                    <line x1={soffitPoints[soffitPoints.length - 1].x + centerX} y1={soffitPoints[soffitPoints.length - 1].y + centerY} x2={soffitPoints[0].x + centerX} y2={soffitPoints[0].y + centerY} stroke="#8b5cf6" strokeWidth={3} strokeDasharray="8,4" className="pointer-events-none" data-id="element-147" />
                  </motion.g>}
            </g>}

        {/* Openings with smart guides when selected */}
        <AnimatePresence data-id="element-148">
          {openings.map(opening => {
          const wall = walls.find(w => w.id === opening.wallId);
          if (!wall) return null;
          const isSelected = selectedObjectId === opening.id;
          const isDragging = isDraggingOpening && draggedOpeningId === opening.id;
          const wallLength = distance(wall.start, wall.end);
          const ratio = opening.position / wallLength;
          const x = wall.start.x + (wall.end.x - wall.start.x) * ratio;
          const y = wall.start.y + (wall.end.y - wall.start.y) * ratio;
          const wallAngle = Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x);
          const angleDeg = wallAngle * 180 / Math.PI;
          // Find other openings on the same wall for spacing dimensions
          const otherOpeningsOnWall = openings.filter(o => o.wallId === opening.wallId && o.id !== opening.id).map(o => ({
            opening: o,
            distance: Math.abs(o.position - opening.position)
          })).sort((a, b) => a.distance - b.distance);
          // Smart guides calculations
          const distanceToStart = opening.position - opening.width / 2;
          const distanceToEnd = wallLength - opening.position - opening.width / 2;
          if (opening.type === 'window') {
            return <motion.g key={opening.id} data-element="opening" data-element-id={opening.id} data-element-type="window" initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: isDragging ? 1.1 : 1
            }} exit={{
              opacity: 0,
              scale: 0.8
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 25
            }} data-id="element-149">
                  {/* Spacing dimensions to other openings on same wall */}
                  {isSelected && otherOpeningsOnWall.length > 0 && <g data-id="element-150">
                      {otherOpeningsOnWall.slice(0, 2).map(({
                  opening: otherOpening,
                  distance: spacingDist
                }) => {
                  const otherRatio = otherOpening.position / wallLength;
                  const otherX = wall.start.x + (wall.end.x - wall.start.x) * otherRatio;
                  const otherY = wall.start.y + (wall.end.y - wall.start.y) * otherRatio;
                  // Calculate edge-to-edge distance
                  const edge1 = opening.position + (otherOpening.position > opening.position ? opening.width / 2 : -opening.width / 2);
                  const edge2 = otherOpening.position + (otherOpening.position > opening.position ? -otherOpening.width / 2 : otherOpening.width / 2);
                  const edgeToEdgeDistance = Math.abs(edge2 - edge1);
                  // Calculate actual edge points on the wall
                  const edge1Ratio = edge1 / wallLength;
                  const edge2Ratio = edge2 / wallLength;
                  const edge1X = wall.start.x + (wall.end.x - wall.start.x) * edge1Ratio;
                  const edge1Y = wall.start.y + (wall.end.y - wall.start.y) * edge1Ratio;
                  const edge2X = wall.start.x + (wall.end.x - wall.start.x) * edge2Ratio;
                  const edge2Y = wall.start.y + (wall.end.y - wall.start.y) * edge2Ratio;
                  const midX = (edge1X + edge2X) / 2;
                  const midY = (edge1Y + edge2Y) / 2;
                  return <g key={`spacing-${otherOpening.id}`} data-id="element-151">
                                {/* Dimension line ON the wall between edges */}
                                <line x1={edge1X + centerX} y1={edge1Y + centerY} x2={edge2X + centerX} y2={edge2Y + centerY} stroke="#10b981" strokeWidth={3} className="pointer-events-none" data-id="element-152" />

                                {/* Edge markers */}
                                <circle cx={edge1X + centerX} cy={edge1Y + centerY} r={3} fill="#10b981" className="pointer-events-none" data-id="element-153" />
                                <circle cx={edge2X + centerX} cy={edge2Y + centerY} r={3} fill="#10b981" className="pointer-events-none" data-id="element-154" />

                                {/* Dimension label ON the wall */}
                                <g className="pointer-events-none" data-id="element-155">
                                  <rect x={midX + centerX - 30} y={midY + centerY - 12} width={60} height={24} fill="#1a1a1a" stroke="#10b981" strokeWidth={1.5} rx={3} opacity={0.95} data-id="element-156" />
                                  <text x={midX + centerX} y={midY + centerY + 5} fill="#10b981" fontSize={12} fontWeight="700" fontFamily="monospace" textAnchor="middle" data-id="element-157">
                                    {formatDistance(edgeToEdgeDistance)}
                                  </text>
                                </g>
                              </g>;
                })}
                    </g>}

                  {/* Width projections on perpendicular walls */}
                  {isSelected && <g data-id="element-158">
                      {(() => {
                  // Find ALL nearest walls (not just perpendicular)
                  const nearestWalls = walls.filter(w => w.id !== wall.id).map(w => {
                    const wallDx = w.end.x - w.start.x;
                    const wallDy = w.end.y - w.start.y;
                    const wallLength = Math.sqrt(wallDx * wallDx + wallDy * wallDy);
                    // Calculate distance from opening center to wall
                    const distToWall = Math.abs(((x - w.start.x) * wallDy - (y - w.start.y) * wallDx) / wallLength);
                    return {
                      wall: w,
                      distance: distToWall
                    };
                  }).sort((a, b) => a.distance - b.distance).slice(0, 4); // Take 4 nearest walls
                  if (nearestWalls.length === 0) return null;
                  const leftEdgeRatio = (opening.position - opening.width / 2) / wallLength;
                  const rightEdgeRatio = (opening.position + opening.width / 2) / wallLength;
                  const leftEdgeX = wall.start.x + (wall.end.x - wall.start.x) * leftEdgeRatio;
                  const leftEdgeY = wall.start.y + (wall.end.y - wall.start.y) * leftEdgeRatio;
                  const rightEdgeX = wall.start.x + (wall.end.x - wall.start.x) * rightEdgeRatio;
                  const rightEdgeY = wall.start.y + (wall.end.y - wall.start.y) * rightEdgeRatio;
                  return nearestWalls.map((nearest, idx) => {
                    const perpWall = nearest.wall;
                    const wallDx = perpWall.end.x - perpWall.start.x;
                    const wallDy = perpWall.end.y - perpWall.start.y;
                    const perpWallLength = Math.sqrt(wallDx * wallDx + wallDy * wallDy);
                    if (perpWallLength === 0) return null;
                    const tLeft = Math.max(0, Math.min(1, ((leftEdgeX - perpWall.start.x) * wallDx + (leftEdgeY - perpWall.start.y) * wallDy) / (perpWallLength * perpWallLength)));
                    const leftProj = {
                      x: perpWall.start.x + tLeft * wallDx,
                      y: perpWall.start.y + tLeft * wallDy
                    };
                    const tRight = Math.max(0, Math.min(1, ((rightEdgeX - perpWall.start.x) * wallDx + (rightEdgeY - perpWall.start.y) * wallDy) / (perpWallLength * perpWallLength)));
                    const rightProj = {
                      x: perpWall.start.x + tRight * wallDx,
                      y: perpWall.start.y + tRight * wallDy
                    };
                    const projDist = Math.sqrt((rightProj.x - leftProj.x) ** 2 + (rightProj.y - leftProj.y) ** 2);
                    if (!isFinite(projDist) || projDist < 5) return null;
                    const midProjX = (leftProj.x + rightProj.x) / 2;
                    const midProjY = (leftProj.y + rightProj.y) / 2;
                    if (!isFinite(midProjX) || !isFinite(midProjY)) return null;
                    return <g key={`width-proj-${perpWall.id}-${idx}`} data-id="element-159">
                              <line x1={leftProj.x + centerX} y1={leftProj.y + centerY} x2={rightProj.x + centerX} y2={rightProj.y + centerY} stroke="#f59e0b" strokeWidth={3} className="pointer-events-none" data-id="element-160" />
                              <circle cx={leftProj.x + centerX} cy={leftProj.y + centerY} r={3} fill="#f59e0b" className="pointer-events-none" data-id="element-161" />
                              <circle cx={rightProj.x + centerX} cy={rightProj.y + centerY} r={3} fill="#f59e0b" className="pointer-events-none" data-id="element-162" />
                              <g className="pointer-events-none" data-id="element-163">
                                <rect x={midProjX + centerX - 30} y={midProjY + centerY - 12} width={60} height={24} fill="#1a1a1a" stroke="#f59e0b" strokeWidth={1.5} rx={3} opacity={0.95} data-id="element-164" />
                                <text x={midProjX + centerX} y={midProjY + centerY + 5} fill="#f59e0b" fontSize={12} fontWeight="700" fontFamily="monospace" textAnchor="middle" data-id="element-165">
                                  {formatDistance(projDist)}
                                </text>
                              </g>
                            </g>;
                  });
                })()}
                    </g>}

                  <g transform={`translate(${x + centerX} ${y + centerY}) rotate(${angleDeg})`} onClick={e => {
                e.stopPropagation();
              }} data-id="element-166">
                    {/* Window frame */}
                    <rect x={-opening.width / 2} y={-15} width={opening.width} height={30} fill="#87CEEB" fillOpacity={0.3} stroke={isSelected ? '#00E5FF' : '#4A90E2'} strokeWidth={isSelected ? 3 : 2} className="cursor-pointer" style={{
                  filter: isSelected ? 'drop-shadow(0 0 8px rgba(0, 229, 255, 0.6))' : undefined
                }} onMouseDown={e => {
                  e.stopPropagation();
                  onSelectObject(opening.id);
                  setIsDraggingOpening(true);
                  setDraggedOpeningId(opening.id);
                }} onClick={e => {
                  e.stopPropagation();
                }} data-id="element-167" />

                    {/* Window divider */}
                    <line x1={0} y1={-15} x2={0} y2={15} stroke={isSelected ? '#00E5FF' : '#4A90E2'} strokeWidth={1.5} className="pointer-events-none" data-id="element-168" />

                    {/* Resize handles - only when selected */}
                    {isSelected && !isDragging && <>
                        {/* Left handle */}
                        <circle cx={-opening.width / 2} cy={0} r={6} fill="#00E5FF" stroke="white" strokeWidth={2} className="cursor-ew-resize" onMouseDown={e => {
                    e.stopPropagation();
                    setIsResizing(true);
                    setResizeElementId(opening.id);
                    setResizeHandle('w');
                    setResizeStartBounds({
                      x: x - opening.width / 2,
                      y: y - 15,
                      width: opening.width,
                      height: 30
                    });
                    const rect = svgRef.current?.getBoundingClientRect();
                    if (rect) {
                      const mouseX = (e.clientX - rect.left) / rect.width * width - centerX;
                      const mouseY = (e.clientY - rect.top) / rect.height * height - centerY;
                      setResizeStartMouse(snapToGrid({
                        x: mouseX,
                        y: mouseY
                      }));
                    }
                  }} data-id="element-169" />

                        {/* Right handle */}
                        <circle cx={opening.width / 2} cy={0} r={6} fill="#00E5FF" stroke="white" strokeWidth={2} className="cursor-ew-resize" onMouseDown={e => {
                    e.stopPropagation();
                    setIsResizing(true);
                    setResizeElementId(opening.id);
                    setResizeHandle('e');
                    setResizeStartBounds({
                      x: x - opening.width / 2,
                      y: y - 15,
                      width: opening.width,
                      height: 30
                    });
                    const rect = svgRef.current?.getBoundingClientRect();
                    if (rect) {
                      const mouseX = (e.clientX - rect.left) / rect.width * width - centerX;
                      const mouseY = (e.clientY - rect.top) / rect.height * height - centerY;
                      setResizeStartMouse(snapToGrid({
                        x: mouseX,
                        y: mouseY
                      }));
                    }
                  }} data-id="element-170" />
                      </>}

                    {/* Window label - editable on double click */}
                    <g className="cursor-pointer" onDoubleClick={e => {
                  e.stopPropagation();
                  setEditingDimension({
                    id: opening.id,
                    type: 'width'
                  });
                }} data-id="element-171">
                      {editingDimension?.id === opening.id && editingDimension?.type === 'width' ? <foreignObject x={-40} y={-45} width={80} height={35} data-id="element-172">
                          <input type="number" defaultValue={Math.round(opening.width)} autoFocus onBlur={e => {
                      const newWidth = Number(e.target.value);
                      if (newWidth > 20) {
                        onUpdateObstacle(opening.id, {
                          width: newWidth
                        });
                      }
                      setEditingDimension(null);
                    }} onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const newWidth = Number((e.target as HTMLInputElement).value);
                        if (newWidth > 20) {
                          onUpdateObstacle(opening.id, {
                            width: newWidth
                          });
                        }
                        setEditingDimension(null);
                      } else if (e.key === 'Escape') {
                        setEditingDimension(null);
                      }
                    }} className="w-full h-full bg-[#2D2D2D] border-2 border-[#4FC3F7] rounded px-2 text-white text-base font-bold text-center focus:outline-none" style={{
                      transform: `rotate(${-angleDeg}deg)`
                    }} data-id="element-173" />
                        </foreignObject> : <text x={0} y={-25} fill={isSelected ? '#4FC3F7' : '#4A90E2'} fontSize={14} fontWeight="700" fontFamily="Inter, sans-serif" textAnchor="middle" className="pointer-events-auto" data-id="element-174">
                          {Math.round(opening.width)} cm
                        </text>}
                    </g>
                  </g>
                </motion.g>;
          } else {
            return <motion.g key={opening.id} data-element="opening" data-element-id={opening.id} data-element-type="door" initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: isDragging ? 1.1 : 1
            }} exit={{
              opacity: 0,
              scale: 0.8
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 25
            }} data-id="element-175">
                  {/* Spacing dimensions to other openings on same wall */}
                  {isSelected && otherOpeningsOnWall.length > 0 && <g data-id="element-176">
                      {otherOpeningsOnWall.slice(0, 2).map(({
                  opening: otherOpening,
                  distance: spacingDist
                }) => {
                  const otherRatio = otherOpening.position / wallLength;
                  const otherX = wall.start.x + (wall.end.x - wall.start.x) * otherRatio;
                  const otherY = wall.start.y + (wall.end.y - wall.start.y) * otherRatio;
                  // Calculate edge-to-edge distance
                  const edge1 = opening.position + (otherOpening.position > opening.position ? opening.width / 2 : -opening.width / 2);
                  const edge2 = otherOpening.position + (otherOpening.position > opening.position ? -otherOpening.width / 2 : otherOpening.width / 2);
                  const edgeToEdgeDistance = Math.abs(edge2 - edge1);
                  const midX = (x + otherX) / 2;
                  const midY = (y + otherY) / 2;
                  // Offset dimension line perpendicular to wall
                  const perpAngle = wallAngle + Math.PI / 2;
                  const offsetDist = 50;
                  const dimX = midX + Math.cos(perpAngle) * offsetDist;
                  const dimY = midY + Math.sin(perpAngle) * offsetDist;
                  return <g key={`spacing-${otherOpening.id}`} data-id="element-177">
                                {/* Guide line from this opening */}
                                <line x1={x + centerX} y1={y + centerY} x2={x + centerX + Math.cos(perpAngle) * offsetDist} y2={y + centerY + Math.sin(perpAngle) * offsetDist} stroke="#10b981" strokeWidth={1} strokeDasharray="2,2" opacity={0.4} className="pointer-events-none" data-id="element-178" />

                                {/* Guide line from other opening */}
                                <line x1={otherX + centerX} y1={otherY + centerY} x2={otherX + centerX + Math.cos(perpAngle) * offsetDist} y2={otherY + centerY + Math.sin(perpAngle) * offsetDist} stroke="#10b981" strokeWidth={1} strokeDasharray="2,2" opacity={0.4} className="pointer-events-none" data-id="element-179" />

                                {/* Dimension line between openings */}
                                <line x1={x + centerX + Math.cos(perpAngle) * offsetDist} y1={y + centerY + Math.sin(perpAngle) * offsetDist} x2={otherX + centerX + Math.cos(perpAngle) * offsetDist} y2={otherY + centerY + Math.sin(perpAngle) * offsetDist} stroke="#10b981" strokeWidth={1.5} className="pointer-events-none" data-id="element-180" />

                                {/* Dimension label */}
                                <g className="pointer-events-none" data-id="element-181">
                                  <rect x={dimX + centerX - 30} y={dimY + centerY - 12} width={60} height={24} fill="#1a1a1a" stroke="#10b981" strokeWidth={1} rx={3} opacity={0.95} data-id="element-182" />
                                  <text x={dimX + centerX} y={dimY + centerY + 5} fill="#10b981" fontSize={11} fontWeight="700" fontFamily="monospace" textAnchor="middle" data-id="element-183">
                                    {formatDistance(edgeToEdgeDistance)}
                                  </text>
                                </g>
                              </g>;
                })}
                    </g>}

                  <g transform={`translate(${x + centerX} ${y + centerY}) rotate(${angleDeg})`} onClick={e => {
                e.stopPropagation();
              }} data-id="element-184">
                    {/* Door frame */}
                    <rect x={-opening.width / 2} y={-15} width={opening.width} height={30} fill="#8B4513" fillOpacity={0.3} stroke={isSelected ? '#00E5FF' : '#A0522D'} strokeWidth={isSelected ? 3 : 2} className="cursor-pointer" style={{
                  filter: isSelected ? 'drop-shadow(0 0 8px rgba(0, 229, 255, 0.6))' : undefined
                }} onMouseDown={e => {
                  e.stopPropagation();
                  onSelectObject(opening.id);
                  setIsDraggingOpening(true);
                  setDraggedOpeningId(opening.id);
                }} onClick={e => {
                  e.stopPropagation();
                }} data-id="element-185" />

                    {/* Door arc (swing) */}
                    <path d={`M ${-opening.width / 2} 15 Q ${-opening.width / 2} ${15 + opening.width / 2} ${-opening.width / 2 + opening.width / 2} ${15 + opening.width / 2}`} fill="none" stroke={isSelected ? '#00E5FF' : '#A0522D'} strokeWidth={1} strokeDasharray="4,2" className="pointer-events-none" data-id="element-186" />

                    {/* Resize handles - only when selected */}
                    {isSelected && !isDragging && <>
                        {/* Left handle */}
                        <circle cx={-opening.width / 2} cy={0} r={6} fill="#00E5FF" stroke="white" strokeWidth={2} className="cursor-ew-resize" onMouseDown={e => {
                    e.stopPropagation();
                    setIsResizing(true);
                    setResizeElementId(opening.id);
                    setResizeHandle('w');
                    setResizeStartBounds({
                      x: x - opening.width / 2,
                      y: y - 15,
                      width: opening.width,
                      height: 30
                    });
                    const rect = svgRef.current?.getBoundingClientRect();
                    if (rect) {
                      const mouseX = (e.clientX - rect.left) / rect.width * width - centerX;
                      const mouseY = (e.clientY - rect.top) / rect.height * height - centerY;
                      setResizeStartMouse(snapToGrid({
                        x: mouseX,
                        y: mouseY
                      }));
                    }
                  }} data-id="element-187" />

                        {/* Right handle */}
                        <circle cx={opening.width / 2} cy={0} r={6} fill="#00E5FF" stroke="white" strokeWidth={2} className="cursor-ew-resize" onMouseDown={e => {
                    e.stopPropagation();
                    setIsResizing(true);
                    setResizeElementId(opening.id);
                    setResizeHandle('e');
                    setResizeStartBounds({
                      x: x - opening.width / 2,
                      y: y - 15,
                      width: opening.width,
                      height: 30
                    });
                    const rect = svgRef.current?.getBoundingClientRect();
                    if (rect) {
                      const mouseX = (e.clientX - rect.left) / rect.width * width - centerX;
                      const mouseY = (e.clientY - rect.top) / rect.height * height - centerY;
                      setResizeStartMouse(snapToGrid({
                        x: mouseX,
                        y: mouseY
                      }));
                    }
                  }} data-id="element-188" />
                      </>}

                    {/* Door label - editable on double click */}
                    <g className="cursor-pointer" onDoubleClick={e => {
                  e.stopPropagation();
                  setEditingDimension({
                    id: opening.id,
                    type: 'width'
                  });
                }} data-id="element-189">
                      {editingDimension?.id === opening.id && editingDimension?.type === 'width' ? <foreignObject x={-40} y={-45} width={80} height={35} data-id="element-190">
                          <input type="number" defaultValue={Math.round(opening.width)} autoFocus onBlur={e => {
                      const newWidth = Number(e.target.value);
                      if (newWidth > 20) {
                        onUpdateObstacle(opening.id, {
                          width: newWidth
                        });
                      }
                      setEditingDimension(null);
                    }} onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const newWidth = Number((e.target as HTMLInputElement).value);
                        if (newWidth > 20) {
                          onUpdateObstacle(opening.id, {
                            width: newWidth
                          });
                        }
                        setEditingDimension(null);
                      } else if (e.key === 'Escape') {
                        setEditingDimension(null);
                      }
                    }} className="w-full h-full bg-[#2D2D2D] border-2 border-[#4FC3F7] rounded px-2 text-white text-base font-bold text-center focus:outline-none" style={{
                      transform: `rotate(${-angleDeg}deg)`
                    }} data-id="element-191" />
                        </foreignObject> : <text x={0} y={-25} fill={isSelected ? '#4FC3F7' : '#A0522D'} fontSize={14} fontWeight="700" fontFamily="Inter, sans-serif" textAnchor="middle" className="pointer-events-auto" data-id="element-192">
                          {Math.round(opening.width)} cm
                        </text>}
                    </g>
                  </g>
                </motion.g>;
          }
        })}
        </AnimatePresence>

        {soffits.map(soffit => {
        const isSelected = selectedObjectId === soffit.id;
        const isDragging = isDraggingSoffit && draggedSoffitId === soffit.id;
        const centerPoint = {
          x: soffit.points.reduce((sum, p) => sum + p.x, 0) / soffit.points.length,
          y: soffit.points.reduce((sum, p) => sum + p.y, 0) / soffit.points.length
        };
        return <g key={soffit.id} data-element="soffit" data-element-id={soffit.id} data-id="element-193">
              {/* Main polygon - clickable for selection and dragging */}
              <polygon points={soffit.points.map(p => `${p.x + centerX},${p.y + centerY}`).join(' ')} fill="rgba(139, 92, 246, 0.05)" stroke={isSelected ? '#4FC3F7' : '#8b5cf6'} strokeWidth={isSelected ? 2 : 1.5} strokeDasharray="8,4" strokeOpacity={0.8} className={isSelected ? 'cursor-move' : 'cursor-pointer'} style={{
            filter: isSelected ? 'drop-shadow(0 0 12px rgba(79, 195, 247, 0.6))' : isDragging ? 'drop-shadow(0 0 8px rgba(139, 92, 246, 0.6))' : undefined
          }} onMouseDown={e => handleSoffitMouseDown(e, soffit.id)} onClick={e => {
            e.stopPropagation();
          }} data-id="element-194" />

              {/* Corner handles - only when selected and not dragging */}
              {isSelected && !isDragging && !isResizing && soffit.points.map((point, i) => <motion.g key={`soffit-handle-${i}`} initial={{
            opacity: 0,
            scale: 0
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25
          }} data-id="element-195">
                    <circle cx={point.x + centerX} cy={point.y + centerY} r={8} fill="#4FC3F7" stroke="white" strokeWidth={2} className="cursor-move" style={{
              pointerEvents: 'all',
              cursor: 'move'
            }} onMouseDown={e => {
              e.stopPropagation();
              e.preventDefault();
              setIsResizing(true);
              setResizeElementId(soffit.id);
              setResizeHandle(`point-${i}` as any);
              const rect = svgRef.current?.getBoundingClientRect();
              if (rect) {
                const mouseX = (e.clientX - rect.left) / rect.width * width - centerX;
                const mouseY = (e.clientY - rect.top) / rect.height * height - centerY;
                setResizeStartMouse(snapToGrid({
                  x: mouseX,
                  y: mouseY
                }));
              }
            }} data-id="element-196" />
                  </motion.g>)}

              {/* Edge handles - NEW: for stretching edges */}
              {isSelected && !isDragging && !isResizing && soffit.points.map((point, i) => {
            const nextPoint = soffit.points[(i + 1) % soffit.points.length];
            const midX = (point.x + nextPoint.x) / 2;
            const midY = (point.y + nextPoint.y) / 2;
            const dx = nextPoint.x - point.x;
            const dy = nextPoint.y - point.y;
            const edgeAngle = Math.atan2(dy, dx) * (180 / Math.PI);
            return <motion.g key={`soffit-edge-handle-${i}`} initial={{
              opacity: 0,
              scale: 0
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 25,
              delay: 0.05
            }} data-id="element-197">
                      {/* Edge handle - square with arrows */}
                      <rect x={midX + centerX - 8} y={midY + centerY - 8} width={16} height={16} fill="#2D2D2D" stroke="#4FC3F7" strokeWidth={2} rx={2} className="cursor-move" style={{
                pointerEvents: 'all',
                cursor: 'move'
              }} onMouseDown={e => handleSoffitEdgeMouseDown(e, soffit.id, i)} data-id="element-198" />

                      {/* Arrow indicators */}
                      <g transform={`translate(${midX + centerX} ${midY + centerY}) rotate(${edgeAngle})`} className="pointer-events-none" data-id="element-199">
                        <path d="M -4 0 L -1 -2 M -4 0 L -1 2" stroke="#4FC3F7" strokeWidth="1.5" strokeLinecap="round" fill="none" data-id="element-200" />
                        <path d="M 4 0 L 1 -2 M 4 0 L 1 2" stroke="#4FC3F7" strokeWidth="1.5" strokeLinecap="round" fill="none" data-id="element-201" />
                      </g>
                    </motion.g>;
          })}

              {/* Edge dimensions - positioned OUTSIDE the soffit - LARGER */}
              {isSelected && !isDragging && soffit.points.map((point, i) => {
            const nextPoint = soffit.points[(i + 1) % soffit.points.length];
            const dx = nextPoint.x - point.x;
            const dy = nextPoint.y - point.y;
            const length = Math.sqrt(dx * dx + dy * dy);
            if (length < 10) return null;
            const midX = (point.x + nextPoint.x) / 2;
            const midY = (point.y + nextPoint.y) / 2;
            const edgeAngle = Math.atan2(dy, dx);
            const perpAngle = edgeAngle + Math.PI / 2;
            const toCenterX = centerPoint.x - midX;
            const toCenterY = centerPoint.y - midY;
            const perpX = Math.cos(perpAngle);
            const perpY = Math.sin(perpAngle);
            const dotProduct = perpX * toCenterX + perpY * toCenterY;
            const offsetDistance = 35;
            const offsetMultiplier = dotProduct > 0 ? -1 : 1;
            const labelX = midX + offsetMultiplier * offsetDistance * Math.cos(perpAngle);
            const labelY = midY + offsetMultiplier * offsetDistance * Math.sin(perpAngle);
            return <g key={`soffit-dim-${i}`} className="pointer-events-none" data-id="element-202">
                      <rect x={labelX + centerX - 35} y={labelY + centerY - 14} width={70} height={28} fill="#2D2D2D" stroke="#4FC3F7" strokeWidth={1.5} rx={4} opacity={0.95} data-id="element-203" />
                      <text x={labelX + centerX} y={labelY + centerY + 6} fill="#4FC3F7" fontSize={15} fontWeight="700" fontFamily="Inter, sans-serif" textAnchor="middle" data-id="element-204">
                        {formatDistance(length)}
                      </text>
                    </g>;
          })}

              {/* Projections to nearest walls - only when selected */}
              {isSelected && !isDragging && <g data-id="element-205">
                  {(() => {
              // Find 2-4 nearest walls
              const nearestWalls = walls.map(wall => {
                const wallDx = wall.end.x - wall.start.x;
                const wallDy = wall.end.y - wall.start.y;
                const wallLength = Math.sqrt(wallDx * wallDx + wallDy * wallDy);
                const distToWall = Math.abs(((centerPoint.x - wall.start.x) * wallDy - (centerPoint.y - wall.start.y) * wallDx) / wallLength);
                return {
                  wall,
                  distance: distToWall
                };
              }).sort((a, b) => a.distance - b.distance).slice(0, 2); // Only show 2 nearest walls instead of 4
              return nearestWalls.map(({
                wall,
                distance
              }, idx) => {
                const wallDx = wall.end.x - wall.start.x;
                const wallDy = wall.end.y - wall.start.y;
                const perpWallLength = Math.sqrt(wallDx * wallDx + wallDy * wallDy);
                if (perpWallLength === 0) return null;
                const edgeProjections = soffit.points.map((point, i) => {
                  const nextPoint = soffit.points[(i + 1) % soffit.points.length];
                  const edgeMidX = (point.x + nextPoint.x) / 2;
                  const edgeMidY = (point.y + nextPoint.y) / 2;
                  const distToWall = Math.abs(((edgeMidX - wall.start.x) * wallDy - (edgeMidY - wall.start.y) * wallDx) / perpWallLength);
                  const tEdge = Math.max(0, Math.min(1, ((edgeMidX - wall.start.x) * wallDx + (edgeMidY - wall.start.y) * wallDy) / (perpWallLength * perpWallLength)));
                  const edgeProjection = {
                    x: wall.start.x + tEdge * wallDx,
                    y: wall.start.y + tEdge * wallDy
                  };
                  return {
                    edgeMid: {
                      x: edgeMidX,
                      y: edgeMidY
                    },
                    projection: edgeProjection,
                    distance: distToWall,
                    edgeIndex: i
                  };
                }).sort((a, b) => a.distance - b.distance)[0];
                if (!edgeProjections) return null;
                return <g key={`projection-${wall.id}-${idx}`} data-id="element-206">
                          {/* Guide line from soffit edge to wall */}
                          <line x1={edgeProjections.edgeMid.x + centerX} y1={edgeProjections.edgeMid.y + centerY} x2={edgeProjections.projection.x + centerX} y2={edgeProjections.projection.y + centerY} stroke="#4FC3F7" strokeWidth={1} strokeDasharray="4,2" opacity={0.5} className="pointer-events-none" data-id="element-207" />

                          {/* Projection marker on wall */}
                          <circle cx={edgeProjections.projection.x + centerX} cy={edgeProjections.projection.y + centerY} r={4} fill="#4FC3F7" opacity={0.7} className="pointer-events-none" data-id="element-208" />

                          {/* Distance label - ONLY THIS, NO WIDTH PROJECTION */}
                          <g className="pointer-events-none" data-id="element-209">
                            <rect x={(edgeProjections.edgeMid.x + edgeProjections.projection.x) / 2 + centerX - 30} y={(edgeProjections.edgeMid.y + edgeProjections.projection.y) / 2 + centerY - 12} width={60} height={24} fill="#2D2D2D" stroke="#4FC3F7" strokeWidth={1.5} rx={3} opacity={0.95} data-id="element-210" />
                            <text x={(edgeProjections.edgeMid.x + edgeProjections.projection.x) / 2 + centerX} y={(edgeProjections.edgeMid.y + edgeProjections.projection.y) / 2 + centerY + 5} fill="#4FC3F7" fontSize={13} fontWeight="700" fontFamily="Inter, sans-serif" textAnchor="middle" data-id="element-211">
                              {formatDistance(edgeProjections.distance)}
                            </text>
                          </g>
                        </g>;
              });
            })()}
                </g>}

              {/* Center label */}
              <g className="pointer-events-none" data-id="element-212">
                <rect x={centerPoint.x + centerX - 70} y={centerPoint.y + centerY - 18} width={140} height={36} fill="#2D2D2D" stroke={isSelected ? '#4FC3F7' : '#8b5cf6'} strokeWidth={1.5} rx={4} opacity={0.95} data-id="element-213" />
                <text x={centerPoint.x + centerX} y={centerPoint.y + centerY + 7} fill={isSelected ? '#4FC3F7' : '#8b5cf6'} fontSize={16} fontWeight="700" fontFamily="Inter, sans-serif" textAnchor="middle" data-id="element-214">
                  {isDragging ? '✋ Ceiling' : `Ceiling H: ${soffit.drop || 30}`}
                </text>
              </g>
            </g>;
      })}

        {/* Obstacles with resize handles and dimension labels */}
        <AnimatePresence data-id="element-215">
          {obstacles.map(obstacle => {
          const isGrabbed = isObstacleGrabbed && grabbedObstacleId === obstacle.id;
          const isSelected = selectedObjectId === obstacle.id;
          const isDragging = isDraggingObstacle && draggedObstacleId === obstacle.id;
          const isOnWall = obstacle.wallId !== undefined;
          // Find two nearest walls for triangulation
          const nearestWalls = walls.map(wall => {
            const distToStart = distance(obstacle.position, wall.start);
            const distToEnd = distance(obstacle.position, wall.end);
            const minDist = Math.min(distToStart, distToEnd);
            return {
              wall,
              distance: minDist
            };
          }).sort((a, b) => a.distance - b.distance).slice(0, 2);
          // Calculate rotation if on wall
          let rotationAngle = 0;
          if (isOnWall && obstacle.wallId) {
            const wall = walls.find(w => w.id === obstacle.wallId);
            if (wall) {
              rotationAngle = Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x) * (180 / Math.PI);
            }
          }
          // Check collision with walls
          const isColliding = walls.some(wall => {
            const wallThickness = wall.thickness || 20;
            const dx = wall.end.x - wall.start.x;
            const dy = wall.end.y - wall.start.y;
            const length = Math.sqrt(dx * dx + dy * dy);
            const perpX = -dy / length * (wallThickness / 2);
            const perpY = dx / length * (wallThickness / 2);
            const distToWall = Math.abs(((obstacle.position.x - wall.start.x) * dy - (obstacle.position.y - wall.start.y) * dx) / length);
            return distToWall < wallThickness / 2 + obstacle.width / 2;
          });
          return <motion.g key={obstacle.id} data-element="obstacle" data-element-id={obstacle.id} initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: isDragging ? 0.5 : 1,
            scale: isGrabbed ? 1.05 : 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 25
          }} data-id="element-216">
                <g transform={`rotate(${rotationAngle} ${obstacle.position.x + centerX} ${obstacle.position.y + centerY})`} data-id="element-217">
                  <rect data-element="obstacle" data-element-id={obstacle.id} x={obstacle.position.x + centerX - obstacle.width / 2} y={obstacle.position.y + centerY - obstacle.depth / 2} width={obstacle.width} height={obstacle.depth} fill={isColliding ? 'rgba(239, 68, 68, 0.3)' : isDragging ? 'rgba(148, 163, 184, 0.3)' : isOnWall ? 'url(#obstacle-pattern-neutral)' : 'url(#obstacle-pattern-neutral)'} stroke={isColliding ? '#ef4444' : isSelected ? '#00E5FF' : isDragging ? '#94A3B8' : isGrabbed ? '#10b981' : '#94A3B8'} strokeWidth={isSelected ? 3 : isDragging ? 2 : isGrabbed ? 2.5 : 2} strokeDasharray={isDragging ? '8,4' : '0'} rx={4} className="cursor-pointer" onMouseDown={e => {
                e.stopPropagation();
                if (activeTool === 'select' || activeTool === 'obstacle') {
                  onSetDraggingObstacle(true);
                  onSetDraggedObstacleId(obstacle.id);
                  onSelectObject(obstacle.id);
                }
              }} onClick={e => {
                e.stopPropagation();
                if (activeTool === 'select' || activeTool === 'obstacle') {
                  onSelectObject(obstacle.id);
                }
              }} style={{
                filter: isSelected ? 'drop-shadow(0 0 12px rgba(0, 229, 255, 0.6))' : isGrabbed ? 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.6))' : undefined,
                pointerEvents: isSelected && !isDragging ? 'none' : 'all'
              }} data-id="element-218" />

                  {/* Subtle label in corner - LARGER */}
                  <text x={obstacle.position.x + centerX - obstacle.width / 2 + 10} y={obstacle.position.y + centerY - obstacle.depth / 2 + 20} fill="#E0E0E0" fontSize={14} fontWeight="600" fontFamily="Inter, sans-serif" className="pointer-events-none" opacity={isDragging ? 0.5 : 1} data-id="element-219">
                    {Math.round(obstacle.width)}×{Math.round(obstacle.depth)}
                  </text>
                </g>

                {/* Triangulation lines when selected */}
                {isSelected && nearestWalls.length >= 2 && !isDragging && <g data-id="element-220">
                    {/* Projections to nearest walls with guide lines */}
                    {nearestWalls.slice(0, 4).map((nearest, idx) => {
                const wall = nearest.wall;
                // Calculate wall direction
                const wallDx = wall.end.x - wall.start.x;
                const wallDy = wall.end.y - wall.start.y;
                const wallLength = Math.sqrt(wallDx * wallDx + wallDy * wallDy);
                // Find projection point on wall from obstacle center
                const t = Math.max(0, Math.min(1, ((obstacle.position.x - wall.start.x) * wallDx + (obstacle.position.y - wall.start.y) * wallDy) / (wallLength * wallLength)));
                const projectionPoint = {
                  x: wall.start.x + t * wallDx,
                  y: wall.start.y + t * wallDy
                };
                // Calculate distance
                const dx = obstacle.position.x - projectionPoint.x;
                const dy = obstacle.position.y - projectionPoint.y;
                const distanceToWall = Math.sqrt(dx * dx + dy * dy);
                // Calculate obstacle corners for width projection
                const obstacleAngle = rotationAngle * (Math.PI / 180);
                const corners = [{
                  x: -obstacle.width / 2,
                  y: -obstacle.depth / 2
                }, {
                  x: obstacle.width / 2,
                  y: -obstacle.depth / 2
                }, {
                  x: obstacle.width / 2,
                  y: obstacle.depth / 2
                }, {
                  x: -obstacle.width / 2,
                  y: obstacle.depth / 2
                }].map(corner => ({
                  x: obstacle.position.x + corner.x * Math.cos(obstacleAngle) - corner.y * Math.sin(obstacleAngle),
                  y: obstacle.position.y + corner.x * Math.sin(obstacleAngle) + corner.y * Math.cos(obstacleAngle)
                }));
                // Project all corners onto the wall
                const cornerProjections = corners.map(corner => {
                  const tCorner = Math.max(0, Math.min(1, ((corner.x - wall.start.x) * wallDx + (corner.y - wall.start.y) * wallDy) / (wallLength * wallLength)));
                  return {
                    x: wall.start.x + tCorner * wallDx,
                    y: wall.start.y + tCorner * wallDy,
                    t: tCorner
                  };
                });
                // Find min and max projections
                const minProj = cornerProjections.reduce((min, p) => p.t < min.t ? p : min);
                const maxProj = cornerProjections.reduce((max, p) => p.t > max.t ? p : max);
                const widthProjection = Math.sqrt((maxProj.x - minProj.x) ** 2 + (maxProj.y - minProj.y) ** 2);
                const midProjX = (minProj.x + maxProj.x) / 2;
                const midProjY = (minProj.y + maxProj.y) / 2;
                return <g key={`obstacle-projection-${wall.id}-${idx}`} data-id="element-221">
                          {/* Guide line from obstacle to wall */}
                          <line x1={obstacle.position.x + centerX} y1={obstacle.position.y + centerY} x2={projectionPoint.x + centerX} y2={projectionPoint.y + centerY} stroke="#00E5FF" strokeWidth={1} strokeDasharray="4,2" opacity={0.5} className="pointer-events-none" data-id="element-222" />

                          {/* Projection marker on wall */}
                          <circle cx={projectionPoint.x + centerX} cy={projectionPoint.y + centerY} r={4} fill="#00E5FF" opacity={0.7} className="pointer-events-none" data-id="element-223" />

                          {/* Distance label */}
                          <g className="pointer-events-none" data-id="element-224">
                            <rect x={(obstacle.position.x + projectionPoint.x) / 2 + centerX - 25} y={(obstacle.position.y + projectionPoint.y) / 2 + centerY - 10} width={50} height={20} fill="#1a1a1a" stroke="#00E5FF" strokeWidth={1} rx={3} opacity={0.95} data-id="element-225" />
                            <text x={(obstacle.position.x + projectionPoint.x) / 2 + centerX} y={(obstacle.position.y + projectionPoint.y) / 2 + centerY + 4} fill="#00E5FF" fontSize={11} fontWeight="700" fontFamily="monospace" textAnchor="middle" data-id="element-226">
                              {formatDistance(distanceToWall)}
                            </text>
                          </g>

                          {/* Width projection on wall - ORANGE */}
                          {widthProjection > 5 && <g data-id="element-227">
                              <line x1={minProj.x + centerX} y1={minProj.y + centerY} x2={maxProj.x + centerX} y2={maxProj.y + centerY} stroke="#f59e0b" strokeWidth={3} className="pointer-events-none" data-id="element-228" />
                              <circle cx={minProj.x + centerX} cy={minProj.y + centerY} r={3} fill="#f59e0b" className="pointer-events-none" data-id="element-229" />
                              <circle cx={maxProj.x + centerX} cy={maxProj.y + centerY} r={3} fill="#f59e0b" className="pointer-events-none" data-id="element-230" />
                              <g className="pointer-events-none" data-id="element-231">
                                <rect x={midProjX + centerX - 30} y={midProjY + centerY - 12} width={60} height={24} fill="#1a1a1a" stroke="#f59e0b" strokeWidth={1.5} rx={3} opacity={0.95} data-id="element-232" />
                                <text x={midProjX + centerX} y={midProjY + centerY + 5} fill="#f59e0b" fontSize={12} fontWeight="700" fontFamily="monospace" textAnchor="middle" data-id="element-233">
                                  {formatDistance(widthProjection)}
                                </text>
                              </g>
                            </g>}
                        </g>;
              })}
                  </g>}

                {isSelected && (activeTool === 'select' || activeTool === 'obstacle') && !isGrabbed && !isDragging && <g transform={`rotate(${rotationAngle} ${obstacle.position.x + centerX} ${obstacle.position.y + centerY})`} data-id="element-234">
                      <ElementResizeHandles x={obstacle.position.x - obstacle.width / 2} y={obstacle.position.y - obstacle.depth / 2} width={obstacle.width} height={obstacle.depth} centerX={centerX} centerY={centerY} onResizeStart={(handle, e) => handleResizeStart(obstacle.id, handle, {
                x: obstacle.position.x - obstacle.width / 2,
                y: obstacle.position.y - obstacle.depth / 2,
                width: obstacle.width,
                height: obstacle.depth
              }, e)} showCorners={true} data-id="element-235" />

                      <g transform={`rotate(${-rotationAngle} ${obstacle.position.x + centerX} ${obstacle.position.y + centerY})`} data-id="element-236">
                        <DimensionLabel x={obstacle.position.x + centerX} y={obstacle.position.y + centerY - obstacle.depth / 2 - 60} value={obstacle.width} label="WIDTH" color="#FFFFFF" onUpdate={newWidth => {
                  onUpdateObstacle(obstacle.id, {
                    width: newWidth
                  });
                }} onClick={e => {
                  e.stopPropagation();
                  setEditingDimension({
                    id: obstacle.id,
                    type: 'width'
                  });
                }} isEditing={editingDimension?.id === obstacle.id && editingDimension?.type === 'width'} onEditComplete={() => setEditingDimension(null)} data-id="element-237" />

                        <DimensionLabel x={obstacle.position.x + centerX + obstacle.width / 2 + 80} y={obstacle.position.y + centerY} value={obstacle.depth} label="DEPTH" color="#FFFFFF" onUpdate={newDepth => {
                  onUpdateObstacle(obstacle.id, {
                    depth: newDepth
                  });
                }} onClick={e => {
                  e.stopPropagation();
                  setEditingDimension({
                    id: obstacle.id,
                    type: 'depth'
                  });
                }} isEditing={editingDimension?.id === obstacle.id && editingDimension?.type === 'depth'} onEditComplete={() => setEditingDimension(null)} data-id="element-238" />
                      </g>
                    </g>}
              </motion.g>;
        })}
        </AnimatePresence>

        {closestWall && (activeTool === 'window' || activeTool === 'door') && <motion.g initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-239">
            {/* Just show a subtle indicator at placement point */}
            <circle cx={(() => {
          const wallLength = distance(closestWall.wall.start, closestWall.wall.end);
          const ratio = closestWall.position / wallLength;
          return closestWall.wall.start.x + (closestWall.wall.end.x - closestWall.wall.start.x) * ratio + centerX;
        })()} cy={(() => {
          const wallLength = distance(closestWall.wall.start, closestWall.wall.end);
          const ratio = closestWall.position / wallLength;
          return closestWall.wall.start.y + (closestWall.wall.end.y - closestWall.wall.start.y) * ratio + centerY;
        })()} r={6} fill={activeTool === 'window' ? '#4A90E2' : '#A0522D'} fillOpacity={0.6} stroke="white" strokeWidth={2} className="pointer-events-none" data-id="element-240" />
          </motion.g>}

        {!closestWall && (activeTool === 'window' || activeTool === 'door') && walls.length > 0 && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.9
      }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#EF5350] text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-lg pointer-events-none" data-id="element-241">
              ⚠ Move closer to a wall to place{' '}
              {activeTool === 'window' ? 'window' : 'door'}
            </motion.div>}

        {isDraggingOpening && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#66BB6A] text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-lg pointer-events-none" data-id="element-242">
            ✋ Dragging - Move along wall or to another wall • Release to place
          </motion.div>}

        {!isDraggingOpening && activeTool !== 'select' && selectedObjectId && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#f59e0b] text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-lg pointer-events-none" data-id="element-243">
            ⚠ Switch to Select tool (V) to drag elements
          </motion.div>}

        {isObstacleGrabbed && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#10b981] text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-lg pointer-events-none" data-id="element-244">
            ✋ Obstacle Grabbed - Move freely or snap to wall • Click to release
          </motion.div>}

        {isDraggingObstacle && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#10b981] text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-lg pointer-events-none" data-id="element-245">
            ✋ Dragging Obstacle - Move freely or snap to wall • Release to
            place
          </motion.div>}

        {isDraggingSoffit && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#8b5cf6] text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-lg pointer-events-none" data-id="element-246">
            ✋ Dragging Ceiling Soffit - Move freely • Release to place
          </motion.div>}

        {isRotatingRoom && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#f59e0b] text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-lg pointer-events-none" data-id="element-247">
            🔄 Rotating Room - Click and drag to rotate • Click button to
            release
          </motion.div>}

        {isMovingRoom && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#10b981] text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-lg pointer-events-none" data-id="element-248">
            ↔️ Moving Room - Click and drag to move • Click button to release
          </motion.div>}

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-[#2D2D2D] border border-[#4A4A4A] rounded-lg px-5 py-3 pointer-events-none" data-id="element-249">
          <div className="text-[#B0B0B0] text-base font-medium" data-id="element-250">
            {activeTool === 'select' && selectedObjectId && !isDraggingOpening && !isDraggingObstacle && 'Drag handles to resize • Click dimensions to edit • Hold and drag element to move'}
            {activeTool === 'select' && !selectedObjectId && 'Click to select • Hold Shift + Drag to move all'}
            {activeTool === 'obstacle' && 'Click to place obstacle • In Select mode: Hold and drag to move'}
            {activeTool === 'window' && closestWall && '✓ Click to place window • In Select mode: Hold and drag to move along wall'}
            {activeTool === 'window' && !closestWall && '⚠ Move closer to a wall to place window'}
            {activeTool === 'door' && closestWall && '✓ Click to place door • In Select mode: Hold and drag to move along wall'}
            {activeTool === 'door' && !closestWall && '⚠ Move closer to a wall to place door'}
            {activeTool === 'wall' && 'Click to draw walls'}
            {activeTool === 'soffit' && 'Click to draw soffit'}
          </div>
        </motion.div>
      </svg>

      {isShiftPressed && activeTool === 'select' && walls.length > 0 && <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.9
    }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#10b981] text-white px-4 py-2 rounded-lg font-mono text-sm font-semibold shadow-lg pointer-events-none" data-id="element-251">
          🔓 Move All Mode - Click & Drag
        </motion.div>}

      {isDraggingOpening && <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#10b981] text-white px-4 py-2 rounded-lg font-mono text-sm font-semibold shadow-lg pointer-events-none" data-id="element-252">
          ✋ Dragging - Move along wall or to another wall • Release to place
        </motion.div>}

      {!isDraggingOpening && activeTool !== 'select' && selectedObjectId && <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#f59e0b] text-white px-4 py-2 rounded-lg font-mono text-sm font-semibold shadow-lg pointer-events-none" data-id="element-253">
          ⚠ Switch to Select tool (V) to drag elements
        </motion.div>}

      {isObstacleGrabbed && <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#10b981] text-white px-4 py-2 rounded-lg font-mono text-sm font-semibold shadow-lg pointer-events-none" data-id="element-254">
          ✋ Obstacle Grabbed - Move freely or snap to wall • Click to release
        </motion.div>}

      {isDraggingObstacle && <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#10b981] text-white px-4 py-2 rounded-lg font-mono text-sm font-semibold shadow-lg pointer-events-none" data-id="element-255">
          ✋ Dragging Obstacle - Move freely or snap to wall • Release to place
        </motion.div>}

      {isDraggingSoffit && <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#8b5cf6] text-white px-4 py-2 rounded-lg font-mono text-sm font-semibold shadow-lg pointer-events-none" data-id="element-256">
          ✋ Dragging Ceiling Soffit - Move freely • Release to place
        </motion.div>}

      {isRotatingRoom && <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#f59e0b] text-white px-4 py-2 rounded-lg font-mono text-sm font-semibold shadow-lg pointer-events-none" data-id="element-257">
          🔄 Rotating Room - Click and drag to rotate • Click button to release
        </motion.div>}

      {isMovingRoom && <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="absolute top-20 left-1/2 -translate-x-1/2 bg-[#10b981] text-white px-4 py-2 rounded-lg font-mono text-sm font-semibold shadow-lg pointer-events-none" data-id="element-258">
          ↔️ Moving Room - Click and drag to move • Click button to release
        </motion.div>}

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-[#2D2D2D] border border-[#4A4A4A] rounded-lg px-5 py-3 pointer-events-none" data-id="element-259">
        <div className="text-[#B0B0B0] text-base font-medium" data-id="element-260">
          {activeTool === 'select' && selectedObjectId && !isDraggingOpening && !isDraggingObstacle && 'Drag handles to resize • Click dimensions to edit • Hold and drag element to move'}
          {activeTool === 'select' && !selectedObjectId && 'Click to select • Hold Shift + Drag to move all'}
          {activeTool === 'obstacle' && 'Click to place obstacle • In Select mode: Hold and drag to move'}
          {activeTool === 'window' && closestWall && '✓ Click to place window • In Select mode: Hold and drag to move along wall'}
          {activeTool === 'window' && !closestWall && '⚠ Move closer to a wall to place window'}
          {activeTool === 'door' && closestWall && '✓ Click to place door • In Select mode: Hold and drag to move along wall'}
          {activeTool === 'door' && !closestWall && '⚠ Move closer to a wall to place door'}
          {activeTool === 'wall' && 'Click to draw walls'}
          {activeTool === 'soffit' && 'Click to draw soffit'}
        </div>
      </motion.div>
    </div>;
}